import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useStateContext } from "contexts/ContextProvider";
import {
  Box,
  useTheme,
  Typography,
  Divider,
  Menu,
  MenuItem,
  useMediaQuery,
  TextField,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  Stack,
  Paper,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import api, { isAbortedRequestError } from "services/api";
import Tabs from "@mui/material/Tabs";
import { sendGraphMail } from "services/graphMailService";

// Removed color code button
import { OVERLAY_NAMES } from "constants/Overlays";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { a11yProps, CustomTabPanel } from "components/CustomTabPanel";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import TaskRoundedIcon from "@mui/icons-material/TaskRounded";
import ForwardToInboxRoundedIcon from "@mui/icons-material/ForwardToInboxRounded";
import CommentRoundedIcon from "@mui/icons-material/CommentRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import {
  formatAmount,
  formatDurationToHHMM,
  formatMileage,
  formatTimeToHHMM12Hr,
  formatDateLong,
  formatDateLongWithTime,
  getStatusColor,
} from "helpers/ServiceHelpers";
import { Card } from "components/Card";
import { Card2 } from "components/Card2";
import { NoteCard } from "components/NoteCard";
import dayjs from "dayjs";
import { useAuth } from "../../../contexts/AuthContext";
import CustomTab from "components/CustomTab";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import History from "../History";
import TaskItem from "components/TaskItem";
import { TaskBoard, buildTaskQueryParams } from "../../workspace/tasks/Tasks";
import { DragDropContext, Draggable } from "@hello-pangea/dnd";
import { usePageTitle } from "hooks/usePageTitle";
import { NAVBAR_HEIGHT } from "constants/layout";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { useWebSocket } from "contexts/WebSocketContext";
import { parsePhoneNumber } from "libphonenumber-js";
import { countryOptions } from "constants/countryData";

// Helper: format a phone number (national format, e.g. (555) 555-1234)
const formatPhoneNumber = (raw, countryCode) => {
  if (!raw) return "";
  try {
    const normalizedRaw = String(raw).trim();
    const withPlus = normalizedRaw.startsWith("+") ? normalizedRaw : `+${countryCode}${normalizedRaw}`;
    const phoneNumber = parsePhoneNumber(withPlus);
    if (phoneNumber && phoneNumber.isValid()) {
      let formatted = phoneNumber.formatNational();
      const cc = String(phoneNumber.countryCallingCode || "");
      // Strip leading trunk prefix (0) for non-US numbers
      if (cc && cc !== "1" && formatted.startsWith("0")) {
        formatted = formatted.slice(1);
      }
      // Include country code prefix for non-US numbers
      if (cc && cc !== "1") {
        formatted = `+${cc} ${formatted}`;
      }
      return formatted;
    }
  } catch {
    // fall through
  }
  return raw;
};

const formatCurrencyValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "$0";
  }

  return `$${value}`;
};

const normalizeCostTypeName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const PARKING_COST_NAME_ALIASES = new Set([
  "parking fees",
  "parking fee",
  "parking fees and tolls",
  "parking fee and tolls",
  "parking fees tolls",
]);

const OFFER_STATUS_LABELS = {
  Offered: "Pending",
  Accepted: "Accepted",
  Declined: "Declined",
  Expired: "Expired",
  Withdrawn: "Withdrawn",
};

const normalizeOfferStatus = (status) => String(status || "").trim();

const getOfferDisplayStatus = (status) => {
  const normalizedStatus = normalizeOfferStatus(status);
  return OFFER_STATUS_LABELS[normalizedStatus] || normalizedStatus || "N/A";
};

const getOfferStatusColor = (theme, status) => {
  switch (normalizeOfferStatus(status)) {
    case "Offered":
      return theme.palette.warning.main;
    case "Accepted":
      return theme.palette.success.main;
    case "Declined":
      return theme.palette.error.main;
    case "Expired":
      return theme.palette.grey[500];
    case "Withdrawn":
      return theme.palette.info.main;
    default:
      return theme.palette.text.secondary;
  }
};

const formatOfferDate = (value) => {
  if (!value) return "N/A";

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : String(value);
};

const formatOfferTime = (value) => {
  if (!value) return "N/A";

  const stringValue = String(value).trim();
  if (!stringValue) return "N/A";

  const directMatch = stringValue.match(
    /^(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*([AP]M))?$/i,
  );
  if (directMatch) {
    let hours = Number(directMatch[1]);
    const minutes = directMatch[2];
    const meridian = directMatch[3]?.toUpperCase() || "";

    if (meridian === "PM" && hours < 12) hours += 12;
    if (meridian === "AM" && hours === 12) hours = 0;

    return `${String(hours).padStart(2, "0")}:${minutes}`;
  }

  const parsed = dayjs(stringValue);
  return parsed.isValid() ? parsed.format("HH:mm") : stringValue;
};

const getOfferServiceType = (service) =>
  String(
    service?.ServiceSubtype?.Name ||
      service?.InterpretationType ||
      service?.ServiceType ||
      service?.Type ||
      service?.ServiceCategory ||
      service?.Mode ||
      "",
  ).trim() || "N/A";

const getOfferAddress = (service) =>
  String(
    service?.InPersonAddress ||
      service?.Address ||
      service?.LocationAddress ||
      service?.Location ||
      "",
  ).trim();

const getOfferSpecialInstructions = (service) =>
  String(
    service?.SpecialInstructions ||
      service?.SpecialInstruction ||
      service?.Instructions ||
      "",
  ).trim();

const isInPersonOffer = (service) => {
  const locationMode = String(
    service?.Mode || service?.LocationType || service?.ServiceMode || "",
  )
    .trim()
    .toLowerCase();

  return Boolean(
    getOfferAddress(service) &&
    (!locationMode || /in[\s-]?person|on[\s-]?site|onsite/.test(locationMode)),
  );
};

export const formatServiceCommentTime = (value) => {
  if (!value) return "N/A";

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("M/D/YYYY h:mm A") : String(value);
};

const getServiceCommentHistoryEntries = (serviceNote) => {
  const sources = [
    serviceNote?.ServiceHistories_FROM,
    serviceNote?.ServiceHistories_TO,
    serviceNote?.ServiceHistories,
    serviceNote?.History,
    serviceNote?.Histories,
  ];

  const entries = sources.flatMap((source) =>
    Array.isArray(source) ? source : [],
  );

  const seen = new Set();
  return entries.filter((entry) => {
    const key =
      entry?.id ||
      `${entry?.Status || ""}-${entry?.ChangedOn || entry?.CreatedOn || entry?.UpdatedOn || ""}-${entry?.Note_FROM || ""}-${entry?.Note_TO || ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeServiceComment = (serviceNote, index) => {
  const historyEntries = getServiceCommentHistoryEntries(serviceNote);
  const createdOn = serviceNote?.CreatedOn || null;
  const updatedOn = serviceNote?.UpdatedOn || serviceNote?.EditedOn || null;

  return {
    id: serviceNote?.id || `service-note-${index}`,
    note: String(serviceNote?.Note || "").trim(),
    author:
      serviceNote?.Member?.Name ||
      serviceNote?.CreatedByMember?.Name ||
      serviceNote?.CreatedByName ||
      "N/A",
    createdOn,
    createdLabel: formatServiceCommentTime(createdOn),
    editedLabel: formatServiceCommentTime(updatedOn),
    hasEditHistory: historyEntries.length > 0 || Boolean(updatedOn),
    historyEntries: historyEntries.map((entry, historyIndex) => ({
      id:
        entry?.id ||
        `${serviceNote?.id || index}-${historyIndex}-${entry?.ChangedOn || entry?.CreatedOn || entry?.UpdatedOn || ""}`,
      label: entry?.Status || entry?.ChangeAction || entry?.Action || "Updated",
      changedOn:
        entry?.ChangedOn || entry?.CreatedOn || entry?.UpdatedOn || null,
      changedBy:
        entry?.Member?.Name ||
        entry?.ChangedByMember?.Name ||
        entry?.CreatedByMember?.Name ||
        entry?.ChangedByName ||
        entry?.CreatedByName ||
        null,
      noteFrom: entry?.Note_FROM ?? entry?.NoteFrom ?? null,
      noteTo: entry?.Note_TO ?? entry?.NoteTo ?? null,
    })),
  };
};

const InterpretationProfile = () => {
  const SERVICE_PROFILE_VIEW_KEY = "SERVICE_PROFILE_VIEW_MODE";
  const [pageTitle, setPageTitle] = useState("Interpretation");
  usePageTitle(pageTitle);
  const { currentUser } = useAuth();
  const canUseModernDetailsView = false;
  const { socket } = useWebSocket();
  const [tabValue, setTabValue] = React.useState(0);
  const [mobileTabValue, setMobileTabValue] = React.useState(0);
  const [useModernDetailsView] = useState(() => {
    try {
      if (!canUseModernDetailsView) return false;
      return localStorage.getItem(SERVICE_PROFILE_VIEW_KEY) === "modern";
    } catch (error) {
      console.error("Failed to read service profile view mode:", error);
      return false;
    }
  });
  const isModernDetailsEnabled =
    canUseModernDetailsView && useModernDetailsView;
  const showMobileOverview = !isModernDetailsEnabled;

  useEffect(() => {
    if (!canUseModernDetailsView) return;
    localStorage.setItem(
      SERVICE_PROFILE_VIEW_KEY,
      useModernDetailsView ? "modern" : "classic",
    );
  }, [canUseModernDetailsView, useModernDetailsView]);

  // Refs for mobile scroll-to-view and scroll spy
  const overviewRef = useRef(null);
  const detailsRef = useRef(null);
  const tasksRef = useRef(null);
  const commentsRef = useRef(null);
  const notesRef = useRef(null);
  const actionsRef = useRef(null);
  const historyRef = useRef(null);
  const mobileScrollRef = useRef(null);
  const mobileTabRef = useRef(0);
  const mobileSectionRefs = useMemo(
    () => [
      overviewRef,
      detailsRef,
      tasksRef,
      notesRef,
      commentsRef,
      actionsRef,
      historyRef,
    ],
    [
      overviewRef,
      detailsRef,
      tasksRef,
      notesRef,
      commentsRef,
      actionsRef,
      historyRef,
    ],
  );

  const [tasksLoading, setTasksLoading] = useState(true);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [tasksForService, setTasksForService] = useState([]);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentDraftError, setCommentDraftError] = useState(false);
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [serviceComments, setServiceComments] = useState([]);
  const commentsListRef = useRef(null);
  const [isSendingGraphEmail, setIsSendingGraphEmail] = useState(false);
  // Removed priority info popover state

  const handleTabsChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMobileTabsChange = (event, newValue) => {
    setMobileTabValue(newValue);
    mobileTabRef.current = newValue;
    const sectionRef =
      mobileSectionRefs[newValue + (showMobileOverview ? 0 : 1)];
    if (sectionRef?.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    // With Overview optional, tabs are: Overview?, Details, Tasks, Notes, Comments, Actions, History
    const maxTabIndex = showMobileOverview ? 6 : 5;
    if (mobileTabRef.current > maxTabIndex) {
      mobileTabRef.current = maxTabIndex;
      setMobileTabValue(maxTabIndex);
    }
  }, [showMobileOverview]);

  const { id } = useParams();

  const location = useLocation();
  const data = location.state;
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Scroll spy effect to update active tab based on scroll position
  useEffect(() => {
    if (!isMobile || !mobileScrollRef.current) return;

    const observerOptions = {
      root: mobileScrollRef.current,
      rootMargin: "-88px 0px -60% 0px",
      threshold: [0.1, 0.25, 0.5, 0.75],
    };

    const observerCallback = (entries) => {
      let mostVisible = null;
      let maxRatio = 0;

      entries.forEach((entry) => {
        if (entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          mostVisible = entry.target;
        }
      });

      if (mostVisible) {
        const index = parseInt(mostVisible.getAttribute("data-index"));
        const tabIndex = Math.max(index - (showMobileOverview ? 0 : 1), 0);
        if (!isNaN(index) && tabIndex !== mobileTabRef.current) {
          mobileTabRef.current = tabIndex;
          setMobileTabValue(tabIndex);
        }
      }
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    mobileSectionRefs.forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [isMobile, mobileSectionRefs, showMobileOverview]);

  const {
    refreshPage,
    triggerPageRefresh,
    showSnackbar,
    setOverlayName,
    overlayData,
    setOverlayData,
    openDialog,
    handleErrorDialog,
    closeDialog,
  } = useStateContext();

  const [listOfClients, setListOfClients] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);
  const [listOfAllInterpretations, setListOfAllInterpretations] = useState([]);
  const [serviceHistories, setServiceHistories] = useState([]);
  const [serviceCostSummary, setServiceCostSummary] = useState(null);
  const [serviceCostLines, setServiceCostLines] = useState([]);

  const [interpretationData, setInterpretationData] = useState({});

  const normalizedServiceComments = useMemo(() => {
    const comments = Array.isArray(serviceComments) ? serviceComments : [];

    const mapped = comments
      .map((c, index) => {
        // comments from server use fields: Comment, Member, Linguist, CreatedAt, EditedAt, EditHistory
        const history = Array.isArray(c.EditHistory) ? c.EditHistory : [];
        const createdOn = c.CreatedAt || c.createdAt || null;
        const updatedOn = c.EditedAt || c.editedAt || null;

        return {
          id: c.id || `service-comment-${index}`,
          note: String(c.Comment || c.CommentText || "").trim(),
          author: c.Member?.Name || c.Linguist?.Name || c.MemberName || "N/A",
          createdOn,
          createdLabel: formatServiceCommentTime(createdOn),
          editedLabel: formatServiceCommentTime(updatedOn),
          hasEditHistory: history.length > 0 || Boolean(updatedOn),
          historyEntries: (history || []).map((entry, historyIndex) => ({
            id: `${c.id || index}-${historyIndex}`,
            label: "Edited",
            changedOn: entry?.editedAt || entry?.editedOn || null,
            changedBy: null,
            noteFrom: entry?.previousComment || entry?.previous || null,
            noteTo: null,
          })),
        };
      })
      .filter((item) => item.note.length > 0)
      .sort((left, right) => {
        const leftTime = dayjs(left.createdOn).valueOf() || 0;
        const rightTime = dayjs(right.createdOn).valueOf() || 0;
        // oldest first (ascending)
        return leftTime - rightTime;
      });

    return mapped;
  }, [serviceComments]);

  // Auto-scroll comments list to bottom when comments change (so newest at bottom is visible)
  useEffect(() => {
    if (!commentsListRef.current) return;
    try {
      // Wait a frame for layout
      requestAnimationFrame(() => {
        const el = commentsListRef.current;
        el.scrollTop = el.scrollHeight;
      });
    } catch (e) {
      // fallback
      const el = commentsListRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [normalizedServiceComments.length]);

  // Also scroll when the Comments tab is opened (desktop or mobile)
  useEffect(() => {
    const commentsDesktopIndex = 3; // desktop tab index for Comments
    const commentsMobileIndex = showMobileOverview ? 4 : 3; // mobile tab index for Comments

    const isCommentsActive =
      (!isMobile && tabValue === commentsDesktopIndex) ||
      (isMobile && mobileTabValue === commentsMobileIndex);
    if (!isCommentsActive) return;
    if (!commentsListRef.current) return;

    try {
      requestAnimationFrame(() => {
        const el = commentsListRef.current;
        el.scrollTop = el.scrollHeight;
      });
    } catch (e) {
      const el = commentsListRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [tabValue, mobileTabValue, isMobile, showMobileOverview]);

  const getDataLists = useCallback(() => {
    api
      .all([
        api.get("/clients/brief"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/interpretations/statuses"),
        api.get("/services/interpretations/types"),
        api.get("/services/interpretations/datesOnly"),
        api.get("/auditLogs/entity/Service/" + id),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            linguistsResponse,
            languagesResponse,
            statusesResponse,
            typesResponse,
            allInterpretationsResponse,
            serviceHistoriesResponse,
          ) => {
            if (clientsResponse) {
              setListOfClients(clientsResponse.data);
            }
            if (linguistsResponse) {
              setListOfLinguists(linguistsResponse.data);
            }
            if (languagesResponse) {
              const tempLanguages = languagesResponse.data.filter(
                (language) => language.Name !== "ALL",
              );
              setListOfLanguages(tempLanguages);
            }
            if (statusesResponse) {
              setListOfStatuses(statusesResponse.data);
            }
            if (typesResponse) {
              const tempLanguages = typesResponse.data.slice(0, 5);
              setListOfTypes(tempLanguages);
            }
            if (allInterpretationsResponse) {
              setListOfAllInterpretations(allInterpretationsResponse.data);
            }
            if (serviceHistoriesResponse) {
              setServiceHistories(
                serviceHistoriesResponse.data?.auditLogs || [],
              );
            }
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";

        handleErrorDialog(message, theme);
      });
  }, [id, handleErrorDialog, theme]);

  const fetchServiceComments = useCallback(async () => {
    if (!id) return;
    try {
      const response = await api.get(`/services/${id}/comments`);
      setServiceComments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      if (!isAbortedRequestError(err)) {
        console.error("Error fetching service comments:", err);
      }
    }
  }, [id]);

  const fetchServiceCostData = useCallback(() => {
    api
      .get(`/serviceCosts/service/${id}/lines`)
      .then((response) => {
        const payload = response.data || {};
        setServiceCostSummary(payload.summary || null);
        setServiceCostLines(Array.isArray(payload.lines) ? payload.lines : []);
      })
      .catch((error) => {
        console.error("Error fetching service cost lines:", error);
        setServiceCostSummary(null);
        setServiceCostLines([]);
      });
  }, [id]);

  const travelTimeCost = useMemo(() => {
    const total = (
      Array.isArray(serviceCostLines) ? serviceCostLines : []
    ).reduce((sum, line) => {
      const sourceLegacyField = String(line?.SourceLegacyField || "").trim();
      const costTypeName = String(
        line?.CostTypeName || line?.CostType?.Name || "",
      )
        .trim()
        .toLowerCase();

      if (
        sourceLegacyField === "TravelTimeCost" ||
        costTypeName === "travel time"
      ) {
        return sum + Number(line?.Amount || 0);
      }
      return sum;
    }, 0);

    return Number(total.toFixed(2));
  }, [serviceCostLines]);

  const parkingFeesCost = useMemo(() => {
    const total = (
      Array.isArray(serviceCostLines) ? serviceCostLines : []
    ).reduce((sum, line) => {
      const sourceLegacyField = String(line?.SourceLegacyField || "").trim();
      const normalizedName = normalizeCostTypeName(
        line?.CostTypeName || line?.CostType?.Name || "",
      );

      if (
        sourceLegacyField === "ParkingFeesTolls" ||
        PARKING_COST_NAME_ALIASES.has(normalizedName)
      ) {
        return sum + Number(line?.Amount || 0);
      }

      return sum;
    }, 0);

    return Number(total.toFixed(2));
  }, [serviceCostLines]);

  const resolvedParkingFeesCost = useMemo(() => {
    const toFiniteNumber = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const candidates = [
      toFiniteNumber(parkingFeesCost),
      toFiniteNumber(serviceCostSummary?.componentBreakdown?.ParkingFeesTolls),
      toFiniteNumber(serviceCostSummary?.componentBreakdown?.ParkingFees),
      toFiniteNumber(interpretationData.ParkingFeesTolls),
    ];

    const firstNonZero = candidates.find(
      (value) => value !== null && value !== 0,
    );
    if (firstNonZero !== undefined) return firstNonZero;

    const firstFinite = candidates.find((value) => value !== null);
    return firstFinite ?? 0;
  }, [
    interpretationData.ParkingFeesTolls,
    parkingFeesCost,
    serviceCostSummary?.componentBreakdown?.ParkingFees,
    serviceCostSummary?.componentBreakdown?.ParkingFeesTolls,
  ]);

  const otherCosts = useMemo(() => {
    const toNumeric = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const items = [
      {
        key: "linguistPay",
        subtitle: "Linguist Pay",
        amount: toNumeric(
          serviceCostSummary?.componentBreakdown?.LinguistPay ??
            interpretationData.LinguistPay,
        ),
      },
      {
        key: "equipment",
        subtitle: "Equipment",
        amount: toNumeric(
          serviceCostSummary?.componentBreakdown?.EquipmentCost ??
            interpretationData.EquipmentCost,
        ),
      },
      {
        key: "additionalLinguist",
        subtitle: "Add. Linguist Cost",
        amount: toNumeric(
          serviceCostSummary?.componentBreakdown?.AdditionalLinguistCost ??
            interpretationData.AdditionalLinguistCost,
        ),
      },
      {
        key: "notarization",
        subtitle: "Notarization Cost",
        amount: toNumeric(
          serviceCostSummary?.componentBreakdown?.NotarizationOrCOTCost ??
            interpretationData.NotarizationOrCOTCost,
        ),
      },
      {
        key: "delivery",
        subtitle: "Delivery Cost",
        amount: toNumeric(
          serviceCostSummary?.componentBreakdown?.DeliveryCost ??
            interpretationData.DeliveryCost,
        ),
      },
      {
        key: "paymentFees",
        subtitle: "Payment Fees",
        amount: toNumeric(
          serviceCostSummary?.componentBreakdown?.PaymentFees ??
            interpretationData.PaymentFees,
        ),
      },
      {
        key: "additionalCost",
        subtitle: "Additional Cost",
        amount: toNumeric(
          serviceCostSummary?.componentBreakdown?.AdditionalCost ??
            interpretationData.AdditionalCost,
        ),
      },
      {
        key: "travelTime",
        subtitle: "Travel Time",
        amount: toNumeric(travelTimeCost),
      },
    ];

    return items
      .map((item) => {
        const hasAmount = item.amount > 0;
        return {
          ...item,
          hasAmount,
          displayValue: hasAmount ? formatAmount(item.amount) : "N/A",
        };
      })
      .sort((left, right) => {
        if (left.hasAmount !== right.hasAmount) {
          return left.hasAmount ? -1 : 1;
        }
        if (left.hasAmount && right.hasAmount && left.amount !== right.amount) {
          return right.amount - left.amount;
        }
        return left.subtitle.localeCompare(right.subtitle);
      });
  }, [
    interpretationData.AdditionalCost,
    interpretationData.AdditionalLinguistCost,
    interpretationData.DeliveryCost,
    interpretationData.EquipmentCost,
    interpretationData.LinguistPay,
    interpretationData.NotarizationOrCOTCost,
    interpretationData.PaymentFees,
    serviceCostSummary?.componentBreakdown?.LinguistPay,
    serviceCostSummary?.componentBreakdown?.AdditionalCost,
    serviceCostSummary?.componentBreakdown?.AdditionalLinguistCost,
    serviceCostSummary?.componentBreakdown?.DeliveryCost,
    serviceCostSummary?.componentBreakdown?.EquipmentCost,
    serviceCostSummary?.componentBreakdown?.NotarizationOrCOTCost,
    serviceCostSummary?.componentBreakdown?.PaymentFees,
    travelTimeCost,
  ]);

  useEffect(() => {
    const fetchData = () => {
      api
        .get(`/services/interpretations/${id}`)
        .then((response) => {
          const interpretation = response.data;

          setPageTitle("Interpretation: " + interpretation.ProjectNumber);

          setInterpretationData(interpretation);
          fetchServiceCostData();
          getDataLists();
          // load comments separately (comments are a different model than notes)
          fetchServiceComments();
        })
        .catch((error) => {
          console.error(
            "[InterpretationProfile] failed fetching interpretation",
            error,
          );
        });
    };

    fetchData();
  }, [
    id,
    refreshPage,
    getDataLists,
    fetchServiceCostData,
    fetchServiceComments,
  ]);

  // Set entity tracking for presence and updates
  const { setCurrentEntity } = usePresence();
  usePagePresence(); // Join entity room when entity is set

  useEffect(() => {
    if (id) {
      const entityId = parseInt(id);

      setCurrentEntity({
        entityType: "Interpretation",
        entityId,
      });
    }
    return () => {
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [id, setCurrentEntity]);

  // Listen for assignment updates from other members
  const handleAssignmentRefetch = useCallback(
    (updatedBy, meta = {}) => {
      // Reload the assignment data
      api
        .get(`/services/interpretations/${id}`)
        .then((response) => {
          setInterpretationData(response.data);
          fetchServiceCostData();
          // refresh comments when assignment is refetched
          fetchServiceComments();
        })
        .catch((err) => console.log("Error refetching assignment:", err));

      const userName = updatedBy?.userName || "Another member";
      const changeType = meta?.changeType || "Assignment";
      const message = `${changeType} updated by ${userName}. Check History for more details.`;
      triggerPageRefresh(message, theme);
    },
    [id, fetchServiceCostData, fetchServiceComments, triggerPageRefresh, theme],
  );

  useAssignmentUpdates({
    modelType: "Interpretation",
    recordId: id ? parseInt(id) : null,
    onUpdate: handleAssignmentRefetch,
  });

  useEffect(() => {
    const fetchTaskEnums = async () => {
      try {
        const [statusesRes] = await Promise.all([
          api.get("/tasks/enums/statuses"),
        ]);
        setTaskStatuses(statusesRes.data || []);
      } catch (error) {
        console.error("Error fetching task enums:", error);
      }
    };

    fetchTaskEnums();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTasksForService = async () => {
      if (!id) return;
      setTasksLoading(true);
      try {
        const params = buildTaskQueryParams(
          {
            entityServiceTypes: ["Interpretation"],
            showAllCompleted: true,
          },
          currentUser?.id,
        );
        params.append("entityId", id);

        const response = await api.get(`/tasks?${params.toString()}`, {
          signal: controller.signal,
        });
        setTasksForService(response.data || []);
      } catch (error) {
        if (isAbortedRequestError(error)) {
          return;
        }
        console.error("Error fetching tasks for service:", error);
        handleErrorDialog("Failed to load tasks", theme);
      } finally {
        if (!controller.signal.aborted) {
          setTasksLoading(false);
        }
      }
    };

    fetchTasksForService();

    return () => {
      controller.abort();
    };
  }, [id, refreshPage, currentUser?.id, handleErrorDialog, theme]);

  // Listen for task updates linked to this service
  useEffect(() => {
    if (!socket || !id) return;

    const handleTaskUpdate = async (data) => {
      // Check if task is linked to this service
      if (
        data.entityType === "Service" &&
        data.serviceType === "Interpretation" &&
        data.entityID === parseInt(id)
      ) {
        // Refetch tasks for this service
        try {
          const params = buildTaskQueryParams(
            {
              entityServiceTypes: ["Interpretation"],
              showAllCompleted: true,
            },
            currentUser?.id,
          );
          params.append("entityId", id);

          const response = await api.get(`/tasks?${params.toString()}`);
          setTasksForService(response.data || []);

          // Show snackbar notification
          const userName = data.updatedBy?.userName || "Unknown User";
          const message = `Task ${data.changeAction} by ${userName}`;
          showSnackbar(message, theme, "info");
        } catch (error) {
          if (!isAbortedRequestError(error)) {
            console.error("Error refreshing tasks:", error);
          }
        }
      }
    };

    socket.on("task:updated", handleTaskUpdate);
    return () => socket.off("task:updated", handleTaskUpdate);
  }, [socket, id, currentUser?.id, showSnackbar, theme]);

  // Listen for service updates (e.g., assignment acceptance)
  useEffect(() => {
    if (!socket || !id) return;

    const handleServiceUpdate = (data) => {
      if (
        parseInt(data.serviceId) === parseInt(id) &&
        data.event === "assignment_accepted"
      ) {
        // Show notification about the accepted assignment
        const message = `Assignment accepted by ${data.acceptedLinguistName || "Linguist"}`;
        showSnackbar(message, theme, "success");

        // Optionally reload service data to show updated LinguistID
        refreshPage();
      }
    };

    socket.on("service:updated", handleServiceUpdate);
    return () => socket.off("service:updated", handleServiceUpdate);
  }, [socket, id, showSnackbar, theme, refreshPage]);

  const navigate = useNavigate();

  // Removed priority info handlers

  const groupTasksByStatus = (taskList) => {
    const grouped = {};
    taskStatuses.forEach((status) => {
      grouped[status] = [];
    });

    taskList.forEach((task) => {
      if (!grouped[task.Status]) {
        grouped[task.Status] = [];
      }
      grouped[task.Status].push(task);
    });

    return grouped;
  };

  const flattenTasksByStatus = (groupedTasks) => {
    const ordered = [];

    taskStatuses.forEach((status) => {
      if (groupedTasks[status]) {
        ordered.push(...groupedTasks[status]);
      }
    });

    Object.keys(groupedTasks).forEach((status) => {
      if (!taskStatuses.includes(status)) {
        ordered.push(...groupedTasks[status]);
      }
    });

    return ordered;
  };

  const handleTaskDragEnd = async (result) => {
    const { destination, draggableId } = result;

    if (!destination) {
      return;
    }

    const newStatus = destination.droppableId;
    const taskId = draggableId.replace("task-", "");

    setTasksForService((prevTasks) => {
      const taskToMove = prevTasks.find(
        (task) => task.id.toString() === taskId,
      );
      if (!taskToMove) return prevTasks;

      const withoutTask = prevTasks.filter(
        (task) => task.id.toString() !== taskId,
      );
      const tasksByStatus = groupTasksByStatus(withoutTask);

      const updatedTask = { ...taskToMove, Status: newStatus };
      const destinationList = tasksByStatus[newStatus] || [];
      const insertAt = Math.min(destination.index, destinationList.length);
      destinationList.splice(insertAt, 0, updatedTask);
      tasksByStatus[newStatus] = destinationList;

      return flattenTasksByStatus(tasksByStatus);
    });

    try {
      await api.patch(`/tasks/updateStatus/${taskId}`, {
        Status: newStatus,
      });

      // Emit socket event for real-time updates
      if (socket) {
        const taskToUpdate = tasksForService.find(
          (t) => t.id.toString() === taskId,
        );
        if (taskToUpdate) {
          socket.emit("task:saved", {
            modelType: "Task",
            recordId: parseInt(taskId),
            entityType: taskToUpdate.EntityType,
            entityID: taskToUpdate.EntityID,
            serviceType: taskToUpdate.ServiceType,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      handleErrorDialog("Failed to update task status", theme);

      const params = buildTaskQueryParams(
        {
          entityServiceTypes: ["Interpretation"],
          showAllCompleted: true,
        },
        currentUser?.id,
      );
      params.append("entityId", id);
      const response = await api.get(`/tasks?${params.toString()}`);
      setTasksForService(response.data || []);
    }
  };

  const renderTaskCard = (task, index) => (
    <Draggable
      draggableId={`task-${task.id}`}
      index={index}
      key={task.id}
      disableInteractiveElementBlocking
    >
      {(provided, snapshot) => (
        <TaskItem
          key={task.id}
          task={task}
          onClick={() => {
            setOverlayData({ Task: task });
            setOverlayName(OVERLAY_NAMES.TASK_PROFILE);
          }}
          provided={provided}
          snapshot={snapshot}
        />
      )}
    </Draggable>
  );

  const targetLanguage =
    interpretationData.ServiceTargetLanguages &&
    interpretationData.ServiceTargetLanguages.length > 0
      ? interpretationData.ServiceTargetLanguages[0]?.Language?.Name
      : "N/A";
  const invoiceNumber = interpretationData.Invoice?.Number || null;
  const invoiceID = interpretationData.Invoice?.id || null;
  const hasLinkedInvoice = Boolean(invoiceID || interpretationData.InvoiceID);
  const canMarkAsPaid = Boolean(
    !interpretationData.Paid && !interpretationData.Invoiced,
  );
  const canMarkAsUnpaid = Boolean(interpretationData.Paid && !hasLinkedInvoice);

  const linguistName = interpretationData.Linguist?.Name?.trim() || "";
  const normalizedLinguistName = linguistName.toLowerCase();
  const isLinguistUnavailable =
    !linguistName ||
    normalizedLinguistName === "n/a" ||
    normalizedLinguistName === "na" ||
    normalizedLinguistName === "none" ||
    normalizedLinguistName === "null" ||
    normalizedLinguistName === "undefined" ||
    normalizedLinguistName === "-";

  const clientID = interpretationData.Client?.id || null;
  const linguistID = isLinguistUnavailable
    ? null
    : interpretationData.Linguist?.id || null;

  // Display status for rendering - show just "Cancelled" if status contains "Cancelled:"
  const displayStatus = (() => {
    const status = interpretationData.InterpretationStatus || "";
    if (status.startsWith("Cancelled")) {
      return "Cancelled";
    }
    return status;
  })();

  const hasMeaningfulValue = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    return Boolean(
      normalized &&
      !["n/a", "na", "none", "null", "undefined", "-"].includes(normalized),
    );
  };

  const buildCallLink = (phoneNumber, ext) => {
    const phone = String(phoneNumber || "").trim();
    const extension = String(ext || "").trim();

    if (!hasMeaningfulValue(phone)) return "";
    if (!hasMeaningfulValue(extension)) return `tel:${phone}`;

    // Pause-based extension format for better dialer compatibility.
    return `tel:${phone},,${extension}`;
  };

  const openCallLink = (phoneNumber, ext) => {
    const callLink = buildCallLink(phoneNumber, ext);
    if (!callLink) return;

    window.open(callLink, "_blank");
  };

  const contactPersonsForActions = (
    Array.isArray(interpretationData.ContactPersons)
      ? interpretationData.ContactPersons
      : []
  )
    .map((contact) => ({
      id: contact?.id || contact?.ContactPersonID || null,
      Name: (contact?.Name || "").trim(),
      PhoneNumber: (contact?.PhoneNumber || "").trim(),
      PhoneNumbers: contact?.PhoneNumbers || [],
      Ext: String(
        contact?.Ext || contact?.ext || contact?.ContactPersonExt || "",
      ).trim(),
      Email: String(contact?.Email || "").trim(),
    }))
    .filter((contact) => contact.Name || contact.PhoneNumber || contact.Email);

  if (
    contactPersonsForActions.length === 0 &&
    interpretationData.ContactPersonName
  ) {
    contactPersonsForActions.push({
      id: null,
      Name: String(interpretationData.ContactPersonName || "").trim(),
      PhoneNumber: String(interpretationData.ContactPersonNumber || "").trim(),
      Ext: String(
        interpretationData.ContactPersonExt ||
          interpretationData.ContactPerson?.Ext ||
          interpretationData.ContactPerson?.ext ||
          "",
      ).trim(),
      Email: String(interpretationData.ContactPerson?.Email || "").trim(),
    });
  }

  const contactPersonsWithPhone = contactPersonsForActions.filter((contact) =>
    hasMeaningfulValue(contact.PhoneNumber),
  );

  const contactPersonsCardItems =
    contactPersonsForActions.length > 0
      ? contactPersonsForActions.map((contact, index) => ({
          subtitle: contact.Name || `Contact ${index + 1}`,
          content: (
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="body2">
                {`Phone: ${contact.PhoneNumber ? formatPhoneNumber(contact.PhoneNumber, "1") : "N/A"}`}
              </Typography>
              {hasMeaningfulValue(contact.Ext) ? (
                <Typography variant="body2">{`Ext: ${contact.Ext}`}</Typography>
              ) : null}
              {hasMeaningfulValue(contact.Email) ? (
                <Typography variant="body2">{`Email: ${contact.Email}`}</Typography>
              ) : null}
            </Box>
          ),
          actions: [
            ...(hasMeaningfulValue(contact.PhoneNumber)
              ? [
                  {
                    key: `call-${contact.id || index}`,
                    label: "Call",
                    icon: <CallRoundedIcon fontSize="small" />,
                    onClick: () =>
                      openCallLink(contact.PhoneNumber, contact.Ext),
                  },
                ]
              : []),
            ...(hasMeaningfulValue(contact.Email)
              ? [
                  {
                    key: `email-${contact.id || index}`,
                    label: "Email",
                    icon: <EmailRoundedIcon fontSize="small" />,
                    onClick: () => {
                      const subject =
                        "Regarding Assignment N° " +
                        interpretationData.ProjectNumber;
                      const mailtoLink = `mailto:${contact.Email}?subject=${encodeURIComponent(subject)}`;
                      window.open(mailtoLink, "_blank");
                    },
                  },
                ]
              : []),
          ],
        }))
      : [
          {
            subtitle: "",
            content: "No contact persons",
            centerContent: true,
          },
        ];

  const offerStatusItems = useMemo(() => {
    const rawOfferCollections = [
      interpretationData.LinguistAssignmentOffers,
      interpretationData.OfferStatuses,
      interpretationData.LinguistOffers,
      interpretationData.Offers,
      interpretationData.AssignmentOffers,
    ];

    const offerCollection = rawOfferCollections.find(
      (collection) => Array.isArray(collection) && collection.length > 0,
    );

    if (offerCollection) {
      return offerCollection.map((offer, index) => ({
        raw: offer,
        id: offer?.id || offer?.LinguistID || offer?.Linguist?.id || index,
        linguistId:
          Number(offer?.LinguistID || offer?.Linguist?.id || 0) || null,
        linguistName:
          offer?.Linguist?.Name ||
          offer?.LinguistName ||
          offer?.Name ||
          `Linguist ${index + 1}`,
        status:
          offer?.Status ||
          offer?.OfferStatus ||
          offer?.LinguistAssignmentStatus ||
          null,
        offeredAt:
          offer?.OfferedAt || offer?.CreatedOn || offer?.CreatedAt || null,
        respondedAt:
          offer?.RespondedAt || offer?.UpdatedOn || offer?.UpdatedAt || null,
        email:
          (offer?.Linguist && offer.Linguist.Email) ||
          offer?.Email ||
          offer?.LinguistEmail ||
          null,
        phone:
          (offer?.Linguist && offer.Linguist.PhoneNumber) ||
          offer?.PhoneNumber ||
          null,
      }));
    }

    if (
      Array.isArray(interpretationData.Linguists) &&
      interpretationData.Linguists.length > 0
    ) {
      return interpretationData.Linguists.map((linguist, index) => ({
        raw: linguist,
        id: linguist?.id || index,
        linguistId: Number(linguist?.id || 0) || null,
        linguistName: linguist?.Name || `Linguist ${index + 1}`,
        status:
          linguist?.Status ||
          linguist?.OfferStatus ||
          linguist?.LinguistAssignmentStatus ||
          null,
        offeredAt: linguist?.OfferedAt || null,
        respondedAt: linguist?.RespondedAt || null,
        email: linguist?.Email || linguist?.LinguistEmail || null,
        phone: linguist?.PhoneNumber || null,
      }));
    }

    return [];
  }, [interpretationData]);

  const handleResendOfferNotification = (offer, method) => {
    // method: 'email' | 'sms'
    const linguistId = offer?.linguistId || null;
    const linguistName = offer?.linguistName || "Linguist";
    if (method === "email") {
      const email =
        offer?.email ||
        offer?.raw?.Linguist?.Email ||
        offer?.raw?.Email ||
        null;
      if (!email) {
        showSnackbar(
          "No email address available for this linguist.",
          theme,
          "warning",
        );
        return;
      }

      // Compose a simple HTML email. You may replace this with a template or overlay for preview.
      const subject = `Regarding Assignment N° ${interpretationData.ProjectNumber}`;
      const htmlContent = `
        <div style="font-family: Arial, Helvetica, sans-serif; color: #111;">
          <p>Hi ${linguistName},</p>
          <p>We're following up regarding Assignment N° <strong>${interpretationData.ProjectNumber}</strong>.</p>
          <p>Please log in to the portal to review the assignment details and respond.</p>
          <p>Thanks,<br/>${currentUser?.Name || "Operations Team"}</p>
        </div>
      `;

      (async () => {
        try {
          setIsSendingGraphEmail(true);
          const payload = {
            toEmail: String(email).trim(),
            toName: linguistName,
            subject,
            htmlContent,
            textContent: `Regarding Assignment N° ${interpretationData.ProjectNumber} - please review in the portal.`,
          };

          const res = await sendGraphMail(payload);
          if (res && res.success) {
            showSnackbar("Email sent successfully.", theme, "success");
          } else {
            showSnackbar("Email queued/sent (no confirmation).", theme, "info");
          }
        } catch (err) {
          console.error("Graph email send failed:", err);
          showSnackbar(
            err.message || "Failed to send email via Graph.",
            theme,
            "error",
          );
        } finally {
          setIsSendingGraphEmail(false);
        }
      })();

      return;
    }

    if (method === "sms") {
      // Open existing send dialog so the team can handle SMS sending logic
      const data = {
        Assignment: interpretationData,
        RecipientLinguist: {
          id: linguistId,
          Name: linguistName,
        },
        PrefillMethod: "sms",
      };
      setOverlayData(data);
      setOverlayName(OVERLAY_NAMES.INTERPRETATION_SEND_DETAILS);
      showSnackbar(
        "Open send dialog to resend SMS to " + linguistName,
        theme,
        "info",
      );
      return;
    }
  };

  // Offer summary removed: render invited linguists list instead.

  const listOfDetails = [
    {
      title: "Client Data",
      items: [
        {
          subtitle: "Client",
          content: interpretationData.Client?.Account || "N/A",
          onClick: clientID
            ? () =>
                navigate(`/clients/${clientID}`, {
                  state: {
                    ComingFrom: `/interpretations/${id}`,
                  },
                })
            : null,
        },
        {
          subtitle: "Requestor",
          content: interpretationData.Requestor?.Name || "N/A",
        },
        {
          subtitle: "Invoicer",
          content: interpretationData.Invoicer?.Name || "N/A",
        },
      ],
    },
    {
      title: "Linguist Data",
      items: [
        {
          subtitle: "Linguist",
          content: isLinguistUnavailable
            ? "N/A"
            : interpretationData.Linguist?.Name,
          onClick: linguistID
            ? () =>
                navigate(`/linguists/${linguistID}`, {
                  state: {
                    ComingFrom: `/interpretations/${id}`,
                  },
                })
            : null,
        },
        { subtitle: "Language", content: targetLanguage || "N/A" },
        {
          subtitle: "Rate",
          content: formatAmount(interpretationData.Rate) || "N/A",
        },
      ],
    },
    {
      title: "Offer Status",
      allowShowMore: true,
      items:
        offerStatusItems.length > 0
          ? offerStatusItems.map((offer) => ({
              subtitle: offer.linguistName,
              multiline: true,
              content: (
                <Stack spacing={0.5} alignItems="flex-end">
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Chip
                      size="small"
                      label={getOfferDisplayStatus(offer.status)}
                      sx={{
                        bgcolor: getOfferStatusColor(theme, offer.status),
                        color: theme.palette.background.default,
                        fontWeight: 700,
                      }}
                    />

                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EmailRoundedIcon fontSize="small" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResendOfferNotification(offer, "email");
                      }}
                      disabled={
                        isSendingGraphEmail ||
                        !(
                          offer?.email ||
                          (offer?.raw &&
                            (offer.raw?.Linguist?.Email ||
                              offer.raw?.Email ||
                              offer.raw?.LinguistEmail))
                        )
                      }
                    >
                      Email
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SmsRoundedIcon fontSize="small" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResendOfferNotification(offer, "sms");
                      }}
                    >
                      SMS
                    </Button>
                  </Box>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    align="right"
                  >
                    {offer.offeredAt
                      ? `Offered ${formatOfferDate(offer.offeredAt)} ${formatOfferTime(offer.offeredAt)}`
                      : offer.status
                        ? getOfferDisplayStatus(offer.status)
                        : "Status unavailable"}
                    {offer.respondedAt
                      ? ` · Updated ${formatOfferDate(offer.respondedAt)} ${formatOfferTime(offer.respondedAt)}`
                      : ""}
                  </Typography>
                </Stack>
              ),
              //onClick: offer.linguistId
              //  ? () =>
              //      navigate(`/linguists/${offer.linguistId}`, {
              //        state: {
              //          ComingFrom: `/interpretations/${id}`,
              //        },
              //      })
              //  : null,
            }))
          : Array.isArray(interpretationData.Linguists) &&
              interpretationData.Linguists.length > 0
            ? interpretationData.Linguists.map((linguist, index) => ({
                subtitle: linguist?.Name || `Linguist ${index + 1}`,
                multiline: true,
                content: (
                  <Stack spacing={0.5} alignItems="flex-end">
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Chip
                        size="small"
                        label={getOfferDisplayStatus(
                          linguist?.OfferStatus || linguist?.Status,
                        )}
                        sx={{
                          bgcolor: getOfferStatusColor(
                            theme,
                            linguist?.OfferStatus || linguist?.Status,
                          ),
                          color: theme.palette.background.default,
                          fontWeight: 700,
                        }}
                      />

                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EmailRoundedIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResendOfferNotification(
                            {
                              raw: linguist,
                              linguistId: linguist?.id,
                              linguistName: linguist?.Name,
                              email: linguist?.Email,
                              phone: linguist?.PhoneNumber,
                            },
                            "email",
                          );
                        }}
                        disabled={
                          isSendingGraphEmail ||
                          !hasMeaningfulValue(linguist?.Email)
                        }
                      >
                        Email
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SmsRoundedIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResendOfferNotification(
                            {
                              raw: linguist,
                              linguistId: linguist?.id,
                              linguistName: linguist?.Name,
                              email: linguist?.Email,
                              phone: linguist?.PhoneNumber,
                            },
                            "sms",
                          );
                        }}
                      >
                        SMS
                      </Button>
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      align="right"
                    >
                      {linguist?.OfferedAt
                        ? `Offered ${formatOfferDate(linguist.OfferedAt)} ${formatOfferTime(linguist.OfferedAt)}`
                        : linguist?.OfferStatus || linguist?.Status
                          ? getOfferDisplayStatus(
                              linguist?.OfferStatus || linguist?.Status,
                            )
                          : "Status unavailable"}
                      {linguist?.RespondedAt
                        ? ` · Updated ${formatOfferDate(linguist.RespondedAt)} ${formatOfferTime(linguist.RespondedAt)}`
                        : ""}
                    </Typography>
                  </Stack>
                ),
                onClick: linguist?.id
                  ? () =>
                      navigate(`/linguists/${linguist.id}`, {
                        state: {
                          ComingFrom: `/interpretations/${id}`,
                        },
                      })
                  : null,
              }))
            : [
                {
                  subtitle: "Offers",
                  content: "No invited linguists or offer records found.",
                  centerContent: true,
                },
              ],
    },
    {
      title: "Contact Persons",
      items: contactPersonsCardItems,
    },
    {
      title: "Assignment",
      items: [
        { subtitle: "Name", content: interpretationData.Name || "N/A" },
        {
          subtitle: "Type",
          content: interpretationData.ServiceSubtype?.Name || "N/A",
        },
        {
          subtitle: "Address",
          content: interpretationData.Address || "N/A",
          actions: interpretationData.Address
            ? [
                {
                  key: "maps",
                  label: "Open in Maps",
                  icon: <RouteRoundedIcon fontSize="small" />,
                  onClick: () => {
                    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      interpretationData.Address,
                    )}`;
                    window.open(mapsUrl, "_blank", "noopener,noreferrer");
                  },
                },
                {
                  key: "copy",
                  label: "Copy address",
                  icon: <ContentCopyRoundedIcon fontSize="small" />,
                  onClick: () => {
                    navigator.clipboard.writeText(interpretationData.Address);
                    showSnackbar(
                      "Address/Link copied to clipboard.",
                      theme,
                      "info",
                    );
                  },
                },
              ]
            : [],
          onClick: interpretationData.Address
            ? () => {
                navigator.clipboard.writeText(interpretationData.Address);
                showSnackbar(
                  "Address/Link copied to clipboard.",
                  theme,
                  "info",
                );
              }
            : null,
        },
      ],
    },
    {
      title: "Duration (Initial)",
      items: [
        {
          subtitle: "Start",
          content: formatTimeToHHMM12Hr(interpretationData.TimeStart),
        },
        {
          subtitle: "End",
          content: formatTimeToHHMM12Hr(interpretationData.TimeEnd),
        },
        {
          subtitle: "Total",
          content: formatDurationToHHMM(interpretationData.Duration),
        },
      ],
    },
    {
      title: "Duration (Actual)",
      items: [
        {
          subtitle: "Start",
          content: formatTimeToHHMM12Hr(interpretationData.ActualStart),
        },
        {
          subtitle: "End",
          content: formatTimeToHHMM12Hr(interpretationData.ActualEnd),
        },
        {
          subtitle: "Total",
          content: formatDurationToHHMM(interpretationData.ActualDuration),
        },
      ],
    },
    {
      title: "Duration (Total)",
      items: [
        {
          subtitle: "Scheduling Time",
          content: formatDurationToHHMM(interpretationData.SchedulingTime),
        },
        {
          subtitle: "Preparation Time",
          content: formatDurationToHHMM(interpretationData.PreparationTime),
        },
        {
          subtitle: "Total",
          content: formatDurationToHHMM(interpretationData.TotalDuration),
        },
      ],
    },
    {
      title: "Members",
      items: [
        {
          subtitle: "Created By",
          content: interpretationData.Creator?.Name || "N/A",
        },
        {
          subtitle: "Checked By",
          content: interpretationData.Checker?.Name || "N/A",
        },
        {
          subtitle: "Checked On",
          content: interpretationData.CheckingDate
            ? dayjs
                .tz(interpretationData.CheckingDate)
                .format("M/D/YYYY h:mm A")
            : "N/A",
        },
      ],
    },
    {
      title: "Statuses",
      items: [
        {
          subtitle: "Paid",
          content: (
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {interpretationData.Paid ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          ),
        },
        {
          subtitle: "Invoiced",
          content: (
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {interpretationData.Invoiced ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          ),
        },
        {
          subtitle: "Cancelled",
          content: (
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {interpretationData.Cancelled ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          ),
        },
      ],
    },
    {
      title: "Costs",
      allowShowMore: true,
      items: [
        {
          subtitle: "Linguist Pay",
          content: formatAmount(
            serviceCostSummary?.componentBreakdown?.LinguistPay ??
              interpretationData.LinguistPay,
          ),
        },
        {
          subtitle: "Profit",
          content: formatAmount(interpretationData.Profit),
        },
        interpretationData.MileageRoundtrip > 0 && {
          subtitle: "Mileage (RT)",
          content: formatMileage(interpretationData.MileageRoundtrip),
        },
        (serviceCostSummary?.componentBreakdown?.MileageCost ||
          interpretationData.MileageCost) > 0 && {
          subtitle: "Mileage Cost",
          content: formatAmount(
            serviceCostSummary?.componentBreakdown?.MileageCost ||
              interpretationData.MileageCost,
          ),
        },
        travelTimeCost > 0 && {
          subtitle: "Travel Time",
          content: formatAmount(travelTimeCost),
        },
        resolvedParkingFeesCost > 0 && {
          subtitle: "Parking Fees",
          content: formatAmount(resolvedParkingFeesCost),
        },
        ...otherCosts
          .filter(
            (c) =>
              !["linguistPay", "travelTime"].includes(c.key) && c.hasAmount,
          )
          .map((c) => ({
            subtitle: c.subtitle,
            content: c.displayValue,
          })),
      ].filter(Boolean),
    },
  ];

  // Add cancellation card if status contains "Cancelled"
  if (
    interpretationData.InterpretationStatus &&
    interpretationData.InterpretationStatus.startsWith("Cancelled")
  ) {
    listOfDetails.push({
      title: interpretationData.InterpretationStatus,
      items: [
        {
          subtitle: "Reason",
          content: interpretationData.CancellationReason || "N/A",
          multiline: true,
          centerContent: false,
        },
      ],
    });
  }

  const contactPersonsIndex = listOfDetails.findIndex(
    (detail) => detail.title === "Contact Persons",
  );
  if (contactPersonsIndex > -1) {
    const [contactPersonsDetail] = listOfDetails.splice(contactPersonsIndex, 1);
    listOfDetails.push(contactPersonsDetail);
  }

  const hasAcceptedLinguist = Array.isArray(offerStatusItems)
    ? offerStatusItems.some((item) => item?.status === "Accepted")
    : false;

  // Reorder details so that Offer Status is at the top, followed by Client Data and Linguist Data
  const offerIndex = listOfDetails.findIndex(
    (detail) => detail.title === "Offer Status",
  );
  if (offerIndex > -1) {
    const [offerDetail] = listOfDetails.splice(offerIndex, 1);

    // Extract Client and Linguist details if present
    const clientIndex = listOfDetails.findIndex(
      (detail) => detail.title === "Client Data",
    );
    const clientDetail =
      clientIndex > -1 ? listOfDetails.splice(clientIndex, 1)[0] : null;

    const linguistIndex = listOfDetails.findIndex(
      (detail) => detail.title === "Linguist Data",
    );
    const linguistDetail =
      linguistIndex > -1 ? listOfDetails.splice(linguistIndex, 1)[0] : null;

    const middle = [];
    if (clientDetail) middle.push(clientDetail);
    if (linguistDetail) middle.push(linguistDetail);

    if (hasAcceptedLinguist) {
      // Offer goes last
      listOfDetails.push(...middle, offerDetail);
    } else {
      // Offer goes first
      listOfDetails.unshift(offerDetail, ...middle);
    }
  }

  const renderLegacyDetails = () => (
    <Grid container spacing={2}>
      {listOfDetails.map((detail, index) => {
        const isOffer = detail.title === "Offer Status";
        const gridSize = isOffer
          ? { xs: 12, lg: 12, xl: 12 }
          : { xs: 12, lg: 12, xl: 6 };

        return (
          <Grid key={index} size={gridSize}>
            <Card2
              title={detail.title}
              items={detail.items}
              justify="start"
              allowShowMore={detail.allowShowMore ?? false}
            />
          </Grid>
        );
      })}
    </Grid>
  );

  // NOTES MENU AND HANDLERS

  const [selectedServiceNote, setSelectedServiceNote] = useState(null);
  // Instead of using an anchor element, we now store the the mouse position for the context menu.
  const [contextMenuPosition, setContextMenuPosition] = useState(null);

  // Handler for editing using the existing logic.
  const handleEdit = (serviceNote) => {
    const data = {
      NoteID: serviceNote.id,
      Note: serviceNote.Note,
      ServiceID: interpretationData.id,
      MemberID: currentUser.id,
      ModelType: "Interpretation",
      Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_EDIT,
    };
    setOverlayData(data);
    setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);
  };

  // Handler for right-click to open context menu at the mouse position.
  const handleContextMenu = (e, serviceNote) => {
    e.preventDefault();
    setSelectedServiceNote(serviceNote);
    setContextMenuPosition({
      top: e.clientY,
      left: e.clientX,
    });
  };

  // Closes the context menu.
  const handleCloseContextMenu = () => {
    setContextMenuPosition(null);
  };

  // Stub for Delete action.
  const handleDelete = (serviceNote) => {
    void serviceNote;

    openDialog({
      theme: theme,
      title: "Confirm Delete",
      message: "Are you sure you want to delete this note?",
      //TO DO ADD dialogIconDelete
      customContent: null,

      tertiaryAction: null,
      secondaryAction: "Close",
      primaryAction: "Delete",

      tertiaryOnClick: null,

      secondaryOnClick: () => {
        closeDialog();
      },

      primaryOnClick: () => {
        deleteNote();
        closeDialog();
      },
    });

    handleCloseContextMenu();
  };

  const handlePostComment = useCallback(async () => {
    const nextComment = commentDraft.trim();
    if (!nextComment) {
      setCommentDraftError(true);
      return;
    }

    if (!interpretationData?.id) return;

    setIsPostingComment(true);

    try {
      // Post as a service comment (separate model from notes)
      const response = await api.post(
        `/services/${interpretationData.id}/comments`,
        {
          comment: nextComment,
        },
      );

      const createdComment = {
        ...(response?.data || {}),
        Member: response?.data?.Member || {
          id: currentUser?.id,
          Name: currentUser?.Name || "You",
        },
      };

      setServiceComments((prev) => [
        createdComment,
        ...(Array.isArray(prev) ? prev : []),
      ]);

      setCommentDraft("");
      setCommentDraftError(false);

      if (socket && interpretationData?.id) {
        socket.emit("assignment:saved", {
          modelType: "Interpretation",
          recordId: interpretationData.id,
          changeType: "Comment",
          changeAction: "Created",
          user: {
            id: currentUser?.id,
            name: currentUser?.Name || "User",
            imageThumbnail: currentUser?.ImageThumbnail,
          },
        });
      }

      showSnackbar("Comment posted successfully", theme, "success");
    } catch (error) {
      console.error("Error posting interpretation comment:", error);
      showSnackbar("Failed to post comment", theme, "error");
    } finally {
      setIsPostingComment(false);
    }
  }, [
    commentDraft,
    currentUser?.ImageThumbnail,
    currentUser?.Name,
    currentUser?.id,
    interpretationData?.id,
    setServiceComments,
    showSnackbar,
    socket,
    theme,
  ]);

  const renderServiceComments = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        gap: 1,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100%",
          borderRadius: "8px",
          bgcolor: theme.palette.background.alt,
        }}
      >
        <Box
          ref={commentsListRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {normalizedServiceComments.length > 0 ? (
            normalizedServiceComments.map((serviceComment, index) => {
              const commentKey =
                serviceComment.id || `${serviceComment.createdOn}-${index}`;
              const isMine =
                currentUser?.Name && serviceComment.author === currentUser.Name;

              return (
                <Box
                  key={commentKey}
                  sx={{
                    display: "flex",
                    justifyContent: isMine ? "flex-end" : "flex-start",
                  }}
                >
                  <Paper
                    variant="outlined"
                    onContextMenu={(event) =>
                      handleContextMenu(event, serviceComment)
                    }
                    sx={{
                      width: "100%",
                      maxWidth: "78%",
                      p: 2,
                      borderRadius: "8px",
                      bgcolor: isMine
                        ? theme.palette.primary.main
                        : theme.palette.background.default,
                      color: isMine
                        ? theme.palette.background.default
                        : theme.palette.primary.text,
                      boxShadow: 1,
                      cursor: "pointer",
                    }}
                  >
                    <Stack spacing={1.25}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: "inherit" }}
                          >
                            {serviceComment.author}
                          </Typography>
                          <Typography variant="caption" color="inherit">
                            {serviceComment.createdLabel}
                          </Typography>
                        </Box>
                        {serviceComment.hasEditHistory ? (
                          <Chip
                            size="small"
                            label="Edited"
                            variant="outlined"
                          />
                        ) : null}
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          color: "inherit",
                        }}
                      >
                        {serviceComment.note}
                      </Typography>

                      {serviceComment.historyEntries?.length > 0 ? (
                        <Box
                          sx={{
                            mt: 0.5,
                            p: 1.25,
                            borderRadius: "8px",
                            bgcolor: theme.palette.action.hover,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, display: "block", mb: 0.75 }}
                          >
                            Edit history
                          </Typography>
                          <Stack spacing={0.5}>
                            {serviceComment.historyEntries?.map(
                              (historyEntry) => (
                                <Box key={historyEntry.id}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block" }}
                                  >
                                    {historyEntry.label}
                                    {historyEntry.changedBy
                                      ? ` · ${historyEntry.changedBy}`
                                      : ""}
                                    {historyEntry.changedOn
                                      ? ` · ${formatServiceCommentTime(historyEntry.changedOn)}`
                                      : ""}
                                  </Typography>
                                  {historyEntry.noteFrom ||
                                  historyEntry.noteTo ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {historyEntry.noteFrom || "(previous)"} →{" "}
                                      {historyEntry.noteTo || "(current)"}
                                    </Typography>
                                  ) : null}
                                </Box>
                              ),
                            )}
                          </Stack>
                        </Box>
                      ) : null}
                    </Stack>
                  </Paper>
                </Box>
              );
            })
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 160,
              }}
            >
              <Typography color="text.secondary">No comments yet.</Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            p: 2,
            bgcolor: theme.palette.background.alt,
          }}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={1}
              maxRows={6}
              placeholder="Write a message..."
              value={commentDraft}
              onChange={(event) => {
                setCommentDraft(event.target.value);
                if (commentDraftError && event.target.value.trim()) {
                  setCommentDraftError(false);
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handlePostComment}
              disabled={isPostingComment || !commentDraft.trim()}
              startIcon={<SendRoundedIcon />}
              sx={{ borderRadius: "8px" }}
            >
              {isPostingComment ? "Posting..." : "Send"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );

  const markAssignmentAsChecked = () => {
    const data = {
      id: interpretationData.id,
      CheckedBy: currentUser.id,

      CheckingDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    };

    api
      .post("/services/markServiceChecked", {
        data: data,
      })
      .then((response) => {
        const dataHistory = {
          Status: "Checked",
          ServiceID: interpretationData.id,
          ProjectNumber: interpretationData.ProjectNumber,

          CheckedBy_FROM: interpretationData?.Checker?.id || null,
          CheckedBy_TO: currentUser.id,

          CheckingDate_FROM: interpretationData.CheckingDate || null,
          CheckingDate_TO: dayjs().format("YYYY-MM-DD HH:mm:ss"),

          ChangedBy: currentUser.id,
          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        };
        void dataHistory;

        triggerPageRefresh(
          "Interpretation Assignment Marked as Checked",
          theme,
        );

        closeDialog();
      })
      .catch((err) => console.log(err));
  };

  const markAssignmentAsPaid = () => {
    const data = {
      id: interpretationData.id,
      Paid: true,
      Invoiced: true,
    };

    api
      .post("/services/markServicePaid", {
        data: data,
      })
      .then((response) => {
        const dataHistory = {
          Status: "Updated",
          ServiceID: interpretationData.id,
          ProjectNumber: interpretationData.ProjectNumber,

          Paid_FROM: interpretationData?.Paid || null,
          Paid_TO: true,

          Invoiced_FROM: interpretationData?.Invoiced || null,
          Invoiced_TO: true,

          ChangedBy: currentUser.id,
          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        };
        void dataHistory;

        triggerPageRefresh(
          "Interpretation Assignment Marked as Checked",
          theme,
        );

        closeDialog();
      })
      .catch((err) => console.log(err));
  };

  const markAssignmentAsUnpaid = () => {
    const data = {
      id: interpretationData.id,
      Paid: false,
      Invoiced: false,
      InvoiceID: null,
    };

    api
      .post("/services/markServicePaid", {
        data: data,
      })
      .then((response) => {
        triggerPageRefresh("Assignment Marked as Unpaid", theme);

        closeDialog();
      })
      .catch((err) => console.log(err));
  };

  const deleteNote = () => {
    api
      .post("/services/updateNote", {
        data: {
          NoteID: selectedServiceNote.id,
          IsDeleted: 1,
        },
      })
      .then((response) => {
        const dataHistory = {
          Status: "Deleted",
          ServiceID: interpretationData.id,
          Note_FROM: selectedServiceNote.id,
          Note_TO: null,
          ChangedBy: currentUser.id,
          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        };
        void dataHistory;

        if (socket && interpretationData?.id) {
          socket.emit("assignment:saved", {
            modelType: "Interpretation",
            recordId: interpretationData.id,
            changeType: "Note",
            changeAction: "Deleted",
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }
        triggerPageRefresh("Note Deleted Sucessfully", theme);
        closeDialog();
      })
      .catch((err) => console.log(err));
  };

  const deleteService = () => {
    const dataOld = {
      Assignment: {
        id: interpretationData.id,
        Deleted: 0,
      },
    };

    const dataNew = {
      Assignment: {
        id: interpretationData.id,
        Deleted: 1,
      },
    };

    const allData = {
      Old: dataOld,
      New: dataNew,
    };

    api
      .post("/services/deleteService", {
        data: allData,
      })
      .then((response) => {
        const historyData = {
          ...response.data,
          Status: "Deleted",
          ChangedBy: currentUser.id,
          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        };
        void historyData;

        triggerPageRefresh(
          "Interpretation Assignment Deleted Sucessfully",
          theme,
        );
        const navBackTo =
          overlayData?.ComingFrom ?? data?.ComingFrom ?? `/interpretations`;

        navigate(`${navBackTo}`);
      })
      .catch((err) => console.log(err));
  };

  const formatTrackerDate = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("M/D/YYYY") : "";
  };

  const sanitizeCell = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    return /["\t\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const showManualCopyDialog = (trackerRow) => {
    openDialog({
      theme,
      title: "Clipboard Blocked",
      message: "", // Render instruction in custom content for consistency
      customContent: (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2">
            Please click the content below and manually copy it, then paste it
            into the Tracker.
          </Typography>
          <Box
            component="textarea"
            value={trackerRow}
            readOnly
            sx={{
              width: "100%",
              minHeight: 160,
              p: 1,
              fontFamily: "monospace",
              fontSize: 14,
              borderRadius: "8px",
              borderColor: theme.palette.divider,
              bgcolor: theme.palette.background.default,
            }}
            onFocus={(e) => e.target.select()}
          />
        </Box>
      ),
      tertiaryAction: null,
      secondaryAction: "Close",
      primaryAction: null,
      tertiaryOnClick: null,
      secondaryOnClick: () => closeDialog(),
      primaryOnClick: null,
    });
  };

  const buildTrackerRow = () => {
    const notesText = (interpretationData.ServiceNotes || [])
      .map((note) => note?.Note)
      .filter(Boolean)
      .join("\n");

    const normalizedMargin =
      interpretationData.Margin === null ||
      interpretationData.Margin === undefined
        ? ""
        : Number(interpretationData.Margin) / 100;

    const projectNumberValue = (() => {
      const num = Number(interpretationData.ProjectNumber);
      if (Number.isFinite(num)) return Math.trunc(num);
      return interpretationData.ProjectNumber || "";
    })();

    const isCompleted = interpretationData.InterpretationStatus === "Completed";

    const columns = [
      projectNumberValue,
      "",
      formatTrackerDate(interpretationData.Date),
      formatTimeToHHMM12Hr(interpretationData.TimeStart),
      formatTimeToHHMM12Hr(interpretationData.TimeEnd),
      formatDurationToHHMM(interpretationData.Duration),
      interpretationData.Client?.Account,
      interpretationData.Requestor?.Name,
      interpretationData.InterpretationStatus,
      interpretationData.AccountCharge,
      interpretationData.Details,
      interpretationData.Linguist?.Name,
      interpretationData.TotalCost,
      interpretationData.AdditionalCost,
      interpretationData.LinguistPay,
      interpretationData.Profit,
      normalizedMargin,
      targetLanguage,
      interpretationData.ServiceSubtype?.Name,
      notesText,
      interpretationData.Address,
      interpretationData.Rate,
      formatDurationToHHMM(interpretationData.TotalDuration),
      formatMileage(interpretationData.MileageRoundtrip),
      interpretationData.MileageCost,
      interpretationData.ParkingFeesTolls,
      formatDurationToHHMM(interpretationData.PreparationTime),
      formatDurationToHHMM(interpretationData.SchedulingTime),
      isCompleted ? formatTimeToHHMM12Hr(interpretationData.ActualStart) : "",
      isCompleted ? formatTimeToHHMM12Hr(interpretationData.ActualEnd) : "",
      isCompleted
        ? formatDurationToHHMM(interpretationData.ActualDuration)
        : "",
      "",
      "",
      interpretationData.CancellationReason,
    ];

    return columns.map(sanitizeCell).join("\t");
  };

  const handleCopyTrackerRow = async () => {
    const trackerRow = buildTrackerRow();

    // If not secure context (e.g., http or direct IP), clipboard API is blocked.
    if (!window.isSecureContext) {
      showManualCopyDialog(trackerRow);
      return;
    }

    try {
      await navigator.clipboard.writeText(trackerRow);
      showSnackbar("Tracker Row copied successfully.", theme, "success");
    } catch (error) {
      console.error("Error copying tracker row:", error);
      showManualCopyDialog(trackerRow);
    }
  };

  const isLink = /^https?:\/\/|^(www\.)|zoom\.|teams\.|meet\.google\.com/.test(
    interpretationData.Address?.toLowerCase(),
  );

  return (
    <Box
      //borderRadius="1.5rem"
      //m="1.5rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`, // Take full viewport height minus margin
      }}
    >
      <Box
        id="test"
        borderRadius="0 0 1.5rem 1.5rem"
        bgcolor={theme.palette.background.alt}
        p={isMobile ? "0.5rem" : "0.5rem 1rem"}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          overflowY: isMobile ? "hidden" : "auto",
          overflowX: "hidden",
        }}
      >
        {isMobile ? (
          // ==================== MOBILE LAYOUT ====================
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
            }}
          >
            <Tabs
              value={mobileTabValue}
              onChange={handleMobileTabsChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                minHeight: "48px",
                bgcolor: theme.palette.background.alt,
                boxShadow: "0 6px 12px -10px rgba(0, 0, 0, 0.35)",
                flexShrink: 0,
              }}
            >
              {showMobileOverview ? <CustomTab label="Overview" /> : null}
              <CustomTab label="Details" />
              <CustomTab label="Tasks" />
              <CustomTab label="Notes" />
              <CustomTab label="Comments" />
              <CustomTab label="Actions" />
              <CustomTab label="History" />
            </Tabs>

            <Box
              ref={mobileScrollRef}
              sx={{
                flex: 1,
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 4,
                minHeight: 0,
                overflowY: "auto",
              }}
            >
              {showMobileOverview ? (
                <Box
                  ref={overviewRef}
                  data-index={0}
                  sx={{ scrollMarginTop: "72px" }}
                >
                  <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                    Overview
                  </Typography>
                  <Grid container spacing={2}>
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12 }}>
                        <Card
                          big
                          subtitle="Number"
                          content={interpretationData.ProjectNumber || "N/A"}
                          theme={theme}
                          onClick={() => {
                            navigator.clipboard.writeText(
                              interpretationData.ProjectNumber,
                            );
                            showSnackbar(
                              "Project number copied to clipboard.",
                              theme,
                              "info",
                            );
                          }}
                        />
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                          subtitle="Client"
                          content={interpretationData.Client?.Account || "N/A"}
                          theme={theme}
                          onClick={
                            clientID
                              ? () =>
                                  navigate(`/clients/${clientID}`, {
                                    state: {
                                      ComingFrom: `/interpretations/${id}`,
                                    },
                                  })
                              : null
                          }
                        />
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                          subtitle="Date"
                          content={formatDateLong(interpretationData.Date)}
                          theme={theme}
                        />
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                          subtitle="Linguist"
                          content={
                            isLinguistUnavailable
                              ? "N/A"
                              : interpretationData.Linguist?.Name
                          }
                          color={
                            isLinguistUnavailable
                              ? theme.palette.grey[500]
                              : undefined
                          }
                          theme={theme}
                          onClick={
                            linguistID
                              ? () =>
                                  navigate(`/linguists/${linguistID}`, {
                                    state: {
                                      ComingFrom: `/interpretations/${id}`,
                                    },
                                  })
                              : null
                          }
                        />
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Card
                        subtitle="Language"
                        content={targetLanguage || "N/A"}
                        theme={theme}
                      />
                    </Grid>

                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12 }}>
                        <Card
                          big
                          subtitle="Status"
                          content={
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 1.25,
                                py: 0.25,
                                borderRadius: "8px",
                                backgroundColor: getStatusColor(
                                  theme,
                                  interpretationData.InterpretationStatus ||
                                    "N/A",
                                ),
                                color: theme.palette.background.default,
                                fontSize: "inherit",
                                fontWeight: 700,
                                lineHeight: 1.2,
                                textAlign: "center",
                              }}
                            >
                              {interpretationData.InterpretationStatus || "N/A"}
                            </Box>
                          }
                          theme={theme}
                        />
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                          subtitle="Revenue"
                          content={formatCurrencyValue(
                            interpretationData.AccountCharge,
                          )}
                          theme={theme}
                        />
                      </Grid>
                    )}
                    {!isModernDetailsEnabled && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                          subtitle="Profit"
                          content={formatCurrencyValue(
                            interpretationData.Profit,
                          )}
                          theme={theme}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ) : null}

              <Box
                ref={detailsRef}
                data-index={1}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  Details
                </Typography>
                {renderLegacyDetails()}
              </Box>

              <Box
                ref={tasksRef}
                data-index={2}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  Tasks
                </Typography>
                <DragDropContext onDragEnd={handleTaskDragEnd}>
                  <TaskBoard
                    statuses={taskStatuses}
                    tasks={tasksForService}
                    isLoading={tasksLoading}
                    onDragEnd={handleTaskDragEnd}
                    renderTaskCard={renderTaskCard}
                    creatingTaskForStatus={null}
                    newTaskTitle=""
                    setNewTaskTitle={() => {}}
                    handleCreateInlineTask={() => {}}
                    setCreatingTaskForStatus={() => {}}
                    enableInlineCreate={false}
                    columnsOverride={1}
                    theme={theme}
                    heightFraction={1}
                    rowHeightFraction={0.8}
                    containerPadding={"0.25rem"}
                    containerMargin={null}
                    gridSpacing={2}
                    externalDragContext={true}
                  />
                </DragDropContext>
              </Box>

              <Box
                ref={notesRef}
                data-index={3}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography
                  variant="h3"
                  sx={{ mb: 2, mt: 3, fontWeight: "bold" }}
                >
                  Notes
                </Typography>
                {interpretationData &&
                Array.isArray(interpretationData.ServiceNotes) &&
                interpretationData.ServiceNotes.length > 0 ? (
                  <Grid container spacing={2}>
                    {interpretationData.ServiceNotes.map(
                      (serviceNote, index) => (
                        <Grid
                          key={serviceNote.id || `${index}`}
                          size={{ xs: 12 }}
                          onContextMenu={(e) =>
                            handleContextMenu(e, serviceNote)
                          }
                          sx={{ cursor: "pointer" }}
                        >
                          <NoteCard
                            title={serviceNote.Note}
                            items={
                              serviceNote.CreatedOn
                                ? (serviceNote?.Member?.Name || "N/A") +
                                  " - " +
                                  formatDateLongWithTime(serviceNote.CreatedOn)
                                : serviceNote?.Member?.Name || "N/A"
                            }
                            justify="start"
                          />
                        </Grid>
                      ),
                    )}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: "center", p: 4 }}>
                    <Typography variant="h4">
                      There are no notes on this assignment, add one by clicking
                      the (+) button
                    </Typography>
                  </Box>
                )}
                <Menu
                  open={Boolean(contextMenuPosition)}
                  onClose={handleCloseContextMenu}
                  anchorReference="anchorPosition"
                  anchorPosition={
                    contextMenuPosition
                      ? {
                          top: contextMenuPosition.top,
                          left: contextMenuPosition.left,
                        }
                      : undefined
                  }
                  slotProps={{
                    paper: {
                      sx: {
                        bgcolor: theme.palette.background.alt,
                        border: `1px solid ${theme.palette.divider}`,
                        "& .MuiListItemIcon-root": {
                          color: theme.palette.primary.main,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleEdit(selectedServiceNote);
                      handleCloseContextMenu();
                    }}
                  >
                    <ListItemIcon>
                      <EditRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit">Edit</Typography>
                  </MenuItem>
                  <MenuItem onClick={() => handleDelete(selectedServiceNote)}>
                    <ListItemIcon>
                      <DeleteRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit">Delete</Typography>
                  </MenuItem>
                </Menu>
              </Box>

              <Box
                ref={commentsRef}
                data-index={4}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography
                  variant="h3"
                  sx={{ mb: 2, mt: 3, fontWeight: "bold" }}
                >
                  Comments
                </Typography>
                {renderServiceComments()}
              </Box>

              <Box
                ref={actionsRef}
                data-index={5}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  Actions
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<EditRoundedIcon />}
                    onClick={() => {
                      const secondData = {
                        ...interpretationData,
                        Action: OVERLAY_NAMES.INTERPRETATION_ACTION_EDIT,
                        Interpretations: listOfAllInterpretations,
                        Clients: listOfClients,
                        Linguists: listOfLinguists,
                        Languages: listOfLanguages,
                        InterpretationStatuses: listOfStatuses,
                        InterpretationTypes: listOfTypes,
                      };
                      setOverlayData(secondData);
                      setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Edit Assignment
                  </Button>

                  {interpretationData.InterpretationStatus &&
                    !(
                      interpretationData.InterpretationStatus.includes(
                        "Completed",
                      ) ||
                      interpretationData.InterpretationStatus.includes(
                        "Cancelled",
                      )
                    ) && (
                      <Button
                        variant="outlined"
                        startIcon={<TaskRoundedIcon />}
                        onClick={() => {
                          const secondData = {
                            ...interpretationData,
                            Action:
                              OVERLAY_NAMES.INTERPRETATION_ACTION_COMPLETE,
                            Interpretations: listOfAllInterpretations,
                            Clients: listOfClients,
                            Linguists: listOfLinguists,
                            Languages: listOfLanguages,
                            InterpretationStatuses: listOfStatuses,
                            InterpretationTypes: listOfTypes,
                          };
                          setOverlayData(secondData);
                          setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);
                        }}
                        fullWidth
                        sx={{ borderRadius: "8px" }}
                      >
                        Complete Assignment
                      </Button>
                    )}

                  <Button
                    variant="outlined"
                    startIcon={<VerifiedUserRoundedIcon />}
                    onClick={() => {
                      openDialog({
                        theme: theme,
                        title: "Mark As Checked",
                        message:
                          "This assignment will be marked as Checked by you on today's date",
                        customContent: null,
                        tertiaryAction: null,
                        secondaryAction: "Close",
                        primaryAction: "Confirm",
                        tertiaryOnClick: null,
                        secondaryOnClick: () => {
                          closeDialog();
                        },
                        primaryOnClick: () => {
                          markAssignmentAsChecked();
                          closeDialog();
                        },
                      });
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Mark As Checked
                  </Button>

                  {canMarkAsPaid ? (
                    <Button
                      variant="outlined"
                      startIcon={<CreditScoreRoundedIcon />}
                      onClick={() => {
                        openDialog({
                          theme: theme,
                          title: "Mark As Paid",
                          message:
                            interpretationData.Client?.Account !==
                            "Private Persons"
                              ? "This assignment will be marked as Paid, and won't be invoiced, are you sure you want to continue?"
                              : "This assignment will be marked as Paid",
                          customContent: null,
                          tertiaryAction: null,
                          secondaryAction: "Close",
                          primaryAction: "Confirm",
                          tertiaryOnClick: null,
                          secondaryOnClick: () => {
                            closeDialog();
                          },
                          primaryOnClick: () => {
                            markAssignmentAsPaid();
                            closeDialog();
                          },
                        });
                      }}
                      fullWidth
                      sx={{ borderRadius: "8px" }}
                    >
                      Mark As Paid
                    </Button>
                  ) : canMarkAsUnpaid ? (
                    <Button
                      variant="outlined"
                      startIcon={<RestartAltRoundedIcon />}
                      onClick={() => {
                        openDialog({
                          theme: theme,
                          title: "Mark As Unpaid",
                          message:
                            "This assignment will be marked as Unpaid and returned to not invoiced status, are you sure you want to continue?",
                          customContent: null,
                          tertiaryAction: null,
                          secondaryAction: "Close",
                          primaryAction: "Confirm",
                          tertiaryOnClick: null,
                          secondaryOnClick: () => {
                            closeDialog();
                          },
                          primaryOnClick: () => {
                            markAssignmentAsUnpaid();
                            closeDialog();
                          },
                        });
                      }}
                      fullWidth
                      sx={{ borderRadius: "8px" }}
                    >
                      Mark As Unpaid
                    </Button>
                  ) : null}

                  <Button
                    variant="outlined"
                    startIcon={<CategoryRoundedIcon />}
                    onClick={() => {
                      const secondData = {
                        ServiceID: interpretationData.id,
                        Category: interpretationData.ServiceCategory,
                      };
                      setOverlayData(secondData);
                      setOverlayName(OVERLAY_NAMES.SERVICE_CHANGE_CATEGORY);
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Change Category
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteRoundedIcon />}
                    onClick={() => {
                      openDialog({
                        theme: theme,
                        title: "Delete Assignment",
                        message:
                          "Do you really wish to delete this assignment?",
                        customContent: null,
                        tertiaryAction: null,
                        secondaryAction: "Close",
                        primaryAction: "Delete",
                        tertiaryOnClick: null,
                        secondaryOnClick: () => {
                          closeDialog();
                        },
                        primaryOnClick: () => {
                          deleteService();
                          closeDialog();
                        },
                      });
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Delete Assignment
                  </Button>

                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Tasks & Notes
                  </Typography>

                  <Button
                    variant="outlined"
                    startIcon={<TaskRoundedIcon />}
                    onClick={() => {
                      setOverlayData({
                        Action: OVERLAY_NAMES.TASK_ACTION_NEW,
                        Task: {
                          EntityType: "Service",
                          EntityID: id,
                          ServiceType: "Interpretation",
                        },
                        Service: {
                          id: interpretationData.id,
                          ProjectNumber: interpretationData.ProjectNumber,
                          Details: interpretationData.Details,
                          ServiceCategory: interpretationData.ServiceCategory,
                        },
                      });
                      setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Add Task
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<CommentRoundedIcon />}
                    onClick={() => {
                      const data = {
                        ServiceID: interpretationData.id,
                        MemberID: currentUser.id,
                        ModelType: "Interpretation",
                        Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_NEW,
                      };
                      setOverlayData(data);
                      setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);
                    }}
                    sx={{ borderRadius: "8px" }}
                    fullWidth
                  >
                    Add Note
                  </Button>

                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Communications
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{ ml: 1, mb: 0.5, display: "block", fontWeight: 600 }}
                  >
                    Assignment
                  </Typography>

                  <Button
                    variant="outlined"
                    startIcon={<ForwardToInboxRoundedIcon />}
                    onClick={() => {
                      setOverlayData({ Assignment: interpretationData });
                      setOverlayName(OVERLAY_NAMES.INTERPRETATION_SEND_DETAILS);
                    }}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Send Details
                  </Button>

                  {interpretationData.Linguist?.Address !== null &&
                    interpretationData.Address !== null &&
                    !isLink && (
                      <Button
                        variant="outlined"
                        startIcon={<RouteRoundedIcon />}
                        onClick={() => {
                          const origin = interpretationData.Linguist.Address;
                          const destination = interpretationData.Address;
                          const googleMapsLink = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
                            origin,
                          )}&destination=${encodeURIComponent(destination)}`;
                          window.open(googleMapsLink, "_blank");
                        }}
                        fullWidth
                        sx={{ borderRadius: "8px" }}
                      >
                        Check Mileage
                      </Button>
                    )}

                  {(interpretationData.Linguist?.Email ||
                    interpretationData.Linguist?.PhoneNumber) && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          ml: 1,
                          mb: 0.5,
                          mt: 1,
                          display: "block",
                          fontWeight: 600,
                        }}
                      >
                        Linguist: {interpretationData.Linguist?.Name || "N/A"}
                      </Typography>

                      {interpretationData.Linguist?.Email !== null && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EmailRoundedIcon />}
                          onClick={() => {
                            const email = interpretationData.Linguist.Email;
                            const subject =
                              "Regarding Assignment N° " +
                              interpretationData.ProjectNumber;
                            const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
                            window.open(mailtoLink, "_blank");
                          }}
                          fullWidth
                          sx={{ borderRadius: "8px" }}
                        >
                          Email
                        </Button>
                      )}

                      {interpretationData.Linguist?.PhoneNumber != null && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CallRoundedIcon />}
                          onClick={() => {
                            const number =
                              interpretationData.Linguist.PhoneNumber;
                            const callLink = `tel:${number}`;
                            window.open(callLink, "_blank");
                          }}
                          fullWidth
                          sx={{ borderRadius: "8px" }}
                        >
                          Call
                        </Button>
                      )}
                    </>
                  )}

                  {(interpretationData.Requestor?.Email ||
                    interpretationData.Requestor?.PhoneNumber) && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          ml: 1,
                          mb: 0.5,
                          mt: 1,
                          display: "block",
                          fontWeight: 600,
                        }}
                      >
                        Requestor: {interpretationData.Requestor?.Name || "N/A"}
                      </Typography>

                      {interpretationData.Requestor?.Email && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EmailRoundedIcon />}
                          onClick={() => {
                            const email = interpretationData.Requestor.Email;
                            const subject =
                              "Regarding Assignment N° " +
                              interpretationData.ProjectNumber;
                            const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
                            window.open(mailtoLink, "_blank");
                          }}
                          fullWidth
                          sx={{ borderRadius: "8px" }}
                        >
                          Email
                        </Button>
                      )}

                      {interpretationData.Requestor?.PhoneNumber && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CallRoundedIcon />}
                          onClick={() => {
                            const number =
                              interpretationData.Requestor.PhoneNumber;
                            const callLink = buildCallLink(
                              number,
                              interpretationData.Requestor?.Ext,
                            );
                            window.open(callLink, "_blank");
                          }}
                          fullWidth
                          sx={{ borderRadius: "8px" }}
                        >
                          Call
                        </Button>
                      )}
                    </>
                  )}

                  {contactPersonsWithPhone.map((contact, index) => (
                    <React.Fragment
                      key={`mobile-contact-call-${contact.id || contact.Name || index}`}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          ml: 1,
                          mb: 0.5,
                          mt: 1,
                          display: "block",
                          fontWeight: 600,
                        }}
                      >
                        Contact Person: {contact.Name || "N/A"}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CallRoundedIcon />}
                        onClick={() => {
                          openCallLink(contact.PhoneNumber, contact.Ext);
                        }}
                        fullWidth
                        sx={{ borderRadius: "8px" }}
                      >
                        Call
                      </Button>
                    </React.Fragment>
                  ))}

                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Utilities
                  </Typography>

                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyRoundedIcon />}
                    onClick={handleCopyTrackerRow}
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  >
                    Copy Tracker Row
                  </Button>
                </Box>
              </Box>

              <Box
                ref={historyRef}
                data-index={6}
                sx={{ scrollMarginTop: "72px" }}
              >
                <Typography variant="h3" sx={{ mb: 2, fontWeight: "bold" }}>
                  History
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <History serviceHistories={serviceHistories} />
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          // ==================== DESKTOP LAYOUT ====================
          <Grid
            container
            spacing={2}
            sx={{
              flex: 1,
              width: "100%",
              height: "100%",
              minHeight: 0,
            }}
          >
            {/* LEFT COLUMN - General Information */}
            <Grid
              size={{ xs: 12, md: "auto" }}
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: "250px",
                width: { md: "300px" },
                maxWidth: { md: "300px" },
                flexShrink: 0,
                height: "100%",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  overflowY: "auto",
                  flex: 1,
                  width: "100%",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    p: ".75rem 0",
                    overflowY: "auto",
                    height: "100%",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h4" fontWeight="bold">
                        General Information
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid container size={{ xs: 12 }} spacing={1}>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Number"
                          content={interpretationData.ProjectNumber}
                          theme={theme}
                          onClick={() => {
                            navigator.clipboard.writeText(
                              interpretationData.ProjectNumber,
                            );
                            showSnackbar(
                              "Project number copied to clipboard.",
                              theme,
                              "info",
                            );
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Status"
                          content={
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 1.25,
                                py: 0.25,
                                borderRadius: "8px",
                                backgroundColor: getStatusColor(
                                  theme,
                                  interpretationData.InterpretationStatus ||
                                    "N/A",
                                ),
                                color: theme.palette.background.default,
                                fontSize: "inherit",
                                fontWeight: 700,
                                lineHeight: 1.2,
                                textAlign: "center",
                              }}
                            >
                              {displayStatus || "N/A"}
                            </Box>
                          }
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Date"
                          content={formatDateLong(interpretationData.Date)}
                          theme={theme}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Invoice"
                          content={
                            interpretationData.Invoice?.Number ||
                            (interpretationData.Invoiced
                              ? interpretationData.Client?.Account ===
                                "Private Persons"
                                ? "Paid Cash"
                                : "Invoiced: Tracker"
                              : "Not Invoiced")
                          }
                          theme={theme}
                          onClick={
                            invoiceNumber
                              ? () =>
                                  navigate(`/invoices/${invoiceID}`, {
                                    state: {
                                      ComingFrom: `/interpretations/${id}`,
                                    },
                                  })
                              : null
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Account Charge"
                          content={formatCurrencyValue(
                            interpretationData.AccountCharge,
                          )}
                          theme={theme}
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          big
                          subtitle="Total Cost"
                          content={formatCurrencyValue(
                            serviceCostSummary?.totalCostFromLines ??
                              interpretationData.TotalCost,
                          )}
                          theme={theme}
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          subtitle="Total Linguist Pay"
                          content={formatCurrencyValue(
                            serviceCostSummary?.componentBreakdown
                              ?.TotalLinguistPay ??
                              interpretationData.TotalLinguistPay,
                          )}
                          theme={theme}
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          subtitle="Profit"
                          content={formatCurrencyValue(
                            interpretationData.Profit,
                          )}
                          theme={theme}
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }} container spacing={1} p=".25rem">
                        <Card
                          subtitle="Margin"
                          content={
                            interpretationData.Margin === 0
                              ? "0%"
                              : interpretationData.Margin === 100
                                ? "100%"
                                : `${interpretationData.Margin}%`
                          }
                          theme={theme}
                          color={
                            interpretationData.Margin < 20
                              ? theme.palette.error.main
                              : interpretationData.Margin < 40
                                ? theme.palette.warning.main
                                : theme.palette.success.main
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: "block" }}
            />

            {/* MIDDLE COLUMN - Details/Tasks/Notes/History Tabs */}
            <Grid
              size={{ xs: 12, md: "grow" }}
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                flex: "1 1 auto",
                height: "100%",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabsChange}
                    aria-label="basic tabs example"
                    sx={{ bgcolor: theme.palette.background.alt }}
                  >
                    <CustomTab label="Details" {...a11yProps(0)} />
                    <CustomTab label="Tasks" {...a11yProps(1)} />
                    <CustomTab label="Notes" {...a11yProps(2)} />
                    <CustomTab label="Comments" {...a11yProps(3)} />
                    <CustomTab label="History" {...a11yProps(4)} />
                  </Tabs>
                </Box>

                <DragDropContext onDragEnd={handleTaskDragEnd}>
                  <CustomTabPanel
                    value={tabValue}
                    index={0}
                    key="Tab One"
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        flexGrow: 1,
                        p: ".75rem 0.25rem",
                        overflowY: "auto",
                        height: "100%",
                      }}
                    >
                      {renderLegacyDetails()}
                    </Box>
                  </CustomTabPanel>

                  <CustomTabPanel
                    value={tabValue}
                    index={1}
                    key="Tab Two"
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "visible",
                      minHeight: 0,
                    }}
                  >
                    <TaskBoard
                      statuses={taskStatuses}
                      tasks={tasksForService}
                      isLoading={tasksLoading}
                      onDragEnd={handleTaskDragEnd}
                      renderTaskCard={renderTaskCard}
                      creatingTaskForStatus={null}
                      newTaskTitle=""
                      setNewTaskTitle={() => {}}
                      handleCreateInlineTask={() => {}}
                      setCreatingTaskForStatus={() => {}}
                      enableInlineCreate={false}
                      columnsOverride={2}
                      theme={theme}
                      heightFraction={1}
                      rowHeightFraction={0.5}
                      containerPadding={"0.25rem"}
                      containerMargin={null}
                      gridSpacing={2}
                      externalDragContext={true}
                    />
                  </CustomTabPanel>

                  <CustomTabPanel
                    value={tabValue}
                    index={2}
                    key="Tab Notes"
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        flexGrow: 1,
                        p: ".75rem 0.25rem",
                        overflowY: "auto",
                        height: "100%",
                      }}
                    >
                      {/* Render existing service notes (internal notes) */}
                      {interpretationData &&
                      Array.isArray(interpretationData.ServiceNotes) &&
                      interpretationData.ServiceNotes.length > 0 ? (
                        <Grid container spacing={2}>
                          {interpretationData.ServiceNotes.map(
                            (serviceNote, index) => (
                              <Grid
                                key={serviceNote.id || `${index}`}
                                size={{ xs: 12 }}
                                onContextMenu={(e) =>
                                  handleContextMenu(e, serviceNote)
                                }
                                sx={{ cursor: "pointer" }}
                              >
                                <NoteCard
                                  title={serviceNote.Note}
                                  items={
                                    serviceNote.CreatedOn
                                      ? (serviceNote?.Member?.Name || "N/A") +
                                        " - " +
                                        formatDateLongWithTime(
                                          serviceNote.CreatedOn,
                                        )
                                      : serviceNote?.Member?.Name || "N/A"
                                  }
                                  justify="start"
                                />
                              </Grid>
                            ),
                          )}
                        </Grid>
                      ) : (
                        <Box sx={{ textAlign: "center", p: 4 }}>
                          <Typography variant="h4">
                            There are no notes on this assignment, add one by
                            clicking the (+) button
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CustomTabPanel>

                  <CustomTabPanel
                    value={tabValue}
                    index={3}
                    key="Tab Comments"
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      minHeight: 0,
                    }}
                  >
                    <Box
                      sx={{
                        flexGrow: 1,
                        p: ".75rem 0.25rem",
                        overflowY: "auto",
                        height: "100%",
                        minHeight: 0,
                      }}
                    >
                      {renderServiceComments()}
                    </Box>
                  </CustomTabPanel>

                  <CustomTabPanel
                    value={tabValue}
                    index={4}
                    key="Tab History"
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      p: 0,
                    }}
                  >
                    <Box
                      sx={{
                        overflowY: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <History serviceHistories={serviceHistories} />
                    </Box>
                  </CustomTabPanel>
                </DragDropContext>
              </Box>
            </Grid>

            <Divider orientation="vertical" flexItem />

            {/* RIGHT COLUMN - Actions */}
            <Grid
              size={{ xs: 12, md: "auto" }}
              sx={{
                display: "flex",
                flexDirection: "column",
                width: { md: "300px" },
                minWidth: { md: "300px" },
                maxWidth: { md: "300px" },
                flexShrink: 0,
                height: "100%",
              }}
            >
              <Box
                sx={{
                  overflowY: "auto",
                  flex: 1,
                  width: "100%",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    p: ".75rem 0",
                    overflowY: "auto",
                    height: "100%",
                  }}
                >
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="h5" fontWeight="bold">
                      Actions
                    </Typography>
                  </Grid>

                  <Divider sx={{ my: 2 }} />
                  <Box width="100%">
                    <Typography
                      sx={{
                        m: "0.25rem 0 0 0.5rem",
                        color: theme.palette.primary.main,
                        fontSize: 13,
                      }}
                    >
                      Assignment
                    </Typography>

                    <ListItem key="Edit" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const secondData = {
                            ...interpretationData,
                            Action: OVERLAY_NAMES.INTERPRETATION_ACTION_EDIT,
                            Interpretations: listOfAllInterpretations,
                            Clients: listOfClients,
                            Linguists: listOfLinguists,
                            Languages: listOfLanguages,
                            InterpretationStatuses: listOfStatuses,
                            InterpretationTypes: listOfTypes,
                          };

                          setOverlayData(secondData);

                          setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <EditRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Edit Assignment" />
                      </ListItemButton>
                    </ListItem>

                    {interpretationData.InterpretationStatus &&
                    (interpretationData.InterpretationStatus.includes(
                      "Completed",
                    ) ||
                      interpretationData.InterpretationStatus.includes(
                        "Cancelled",
                      )) ? null : (
                      <ListItem key="Complete" disablePadding>
                        <ListItemButton
                          onClick={() => {
                            const secondData = {
                              ...interpretationData,
                              Action:
                                OVERLAY_NAMES.INTERPRETATION_ACTION_COMPLETE,
                              Interpretations: listOfAllInterpretations,
                              Clients: listOfClients,
                              Linguists: listOfLinguists,
                              Languages: listOfLanguages,
                              InterpretationStatuses: listOfStatuses,
                              InterpretationTypes: listOfTypes,
                            };

                            setOverlayData(secondData);

                            setOverlayName(
                              OVERLAY_NAMES.INTERPRETATION_NEW_EDIT,
                            );
                          }}
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <TaskRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Complete Assignment" />
                        </ListItemButton>
                      </ListItem>
                    )}

                    <ListItem key="Check" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          openDialog({
                            theme: theme,
                            title: "Mark As Checked",
                            message:
                              "This assignment will be marked as Checked by you on today's date",
                            customContent: null,

                            tertiaryAction: null,
                            secondaryAction: "Close",
                            primaryAction: "Confirm",

                            tertiaryOnClick: null,

                            secondaryOnClick: () => {
                              closeDialog();
                            },

                            primaryOnClick: () => {
                              markAssignmentAsChecked();
                              closeDialog();
                            },
                          });
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <VerifiedUserRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Mark As Checked" />
                      </ListItemButton>
                    </ListItem>

                    {canMarkAsPaid ? (
                      <ListItem key="Pay" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            openDialog({
                              theme: theme,
                              title: "Mark As Paid",
                              message:
                                interpretationData.Client?.Account !==
                                "Private Persons"
                                  ? "This assignment will be marked as Paid, and won't be invoiced, are you sure you want to continue?"
                                  : "This assignment will be marked as Paid",
                              customContent: null,

                              tertiaryAction: null,
                              secondaryAction: "Close",
                              primaryAction: "Confirm",

                              tertiaryOnClick: null,

                              secondaryOnClick: () => {
                                closeDialog();
                              },

                              primaryOnClick: () => {
                                markAssignmentAsPaid();
                                closeDialog();
                              },
                            });
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <CreditScoreRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Mark As Paid" />
                        </ListItemButton>
                      </ListItem>
                    ) : canMarkAsUnpaid ? (
                      <ListItem key="Unpay" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            openDialog({
                              theme: theme,
                              title: "Mark As Unpaid",
                              message:
                                "This assignment will be marked as Unpaid and returned to not invoiced status, are you sure you want to continue?",
                              customContent: null,

                              tertiaryAction: null,
                              secondaryAction: "Close",
                              primaryAction: "Confirm",

                              tertiaryOnClick: null,

                              secondaryOnClick: () => {
                                closeDialog();
                              },

                              primaryOnClick: () => {
                                markAssignmentAsUnpaid();
                                closeDialog();
                              },
                            });
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <RestartAltRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Mark As Unpaid" />
                        </ListItemButton>
                      </ListItem>
                    ) : null}

                    <ListItem key="ChangeCategory" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const secondData = {
                            ServiceID: interpretationData.id,
                            Category: interpretationData.ServiceCategory,
                          };

                          setOverlayData(secondData);

                          setOverlayName(OVERLAY_NAMES.SERVICE_CHANGE_CATEGORY);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <CategoryRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Change Category" />
                      </ListItemButton>
                    </ListItem>

                    <ListItem key="DeleteAssignment" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "12px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          openDialog({
                            theme: theme,
                            title: "Delete Assignment",
                            message:
                              "Do you really wish to delete this assignment?",
                            customContent: null,

                            tertiaryAction: null,
                            secondaryAction: "Close",
                            primaryAction: "Delete",

                            tertiaryOnClick: null,

                            secondaryOnClick: () => {
                              closeDialog();
                            },

                            primaryOnClick: () => {
                              deleteService();
                              closeDialog();
                            },
                          });
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <DeleteRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Delete Assignment" />
                      </ListItemButton>
                    </ListItem>

                    <Divider sx={{ my: 2 }} />

                    <Typography
                      sx={{
                        m: "0.25rem 0 0 0.5rem",
                        color: theme.palette.primary.main,
                        fontSize: 13,
                      }}
                    >
                      Tasks & Notes
                    </Typography>

                    <ListItem key="AddTask" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          setOverlayData({
                            Action: OVERLAY_NAMES.TASK_ACTION_NEW,
                            Task: {
                              EntityType: "Service",
                              EntityID: id,
                              ServiceType: "Interpretation",
                            },
                            Service: {
                              id: interpretationData.id,
                              ProjectNumber: interpretationData.ProjectNumber,
                              Details: interpretationData.Details,
                              ServiceCategory:
                                interpretationData.ServiceCategory,
                            },
                          });
                          setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <TaskRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Add Task" />
                      </ListItemButton>
                    </ListItem>

                    <ListItem key="AddNote" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          const data = {
                            ServiceID: interpretationData.id,
                            MemberID: currentUser.id,
                            ModelType: "Interpretation",
                            Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_NEW,
                          };

                          setOverlayData(data);

                          setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <CommentRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Add Note" />
                      </ListItemButton>
                    </ListItem>

                    <Divider sx={{ my: 2 }} />

                    <Typography
                      sx={{
                        m: "0.25rem 0 0 0.5rem",
                        color: theme.palette.primary.main,
                        fontSize: 13,
                      }}
                    >
                      Communications
                    </Typography>

                    <Typography
                      sx={{
                        m: "0.5rem 0 0 0.75rem",
                        color: theme.palette.primary.main,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      Assignment
                    </Typography>

                    <ListItem key="Send Details" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={() => {
                          setOverlayData({
                            Assignment: interpretationData,
                          });
                          setOverlayName(
                            OVERLAY_NAMES.INTERPRETATION_SEND_DETAILS,
                          );
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <ForwardToInboxRoundedIcon
                            sx={{
                              fontSize: 28,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Send Details" />
                      </ListItemButton>
                    </ListItem>

                    {interpretationData.Linguist?.Address !== null &&
                    interpretationData.Address !== null &&
                    !isLink ? (
                      <ListItem key="Mileage Check" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            const origin = interpretationData.Linguist.Address;
                            const destination = interpretationData.Address;

                            const googleMapsLink = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
                              origin,
                            )}&destination=${encodeURIComponent(destination)}`;

                            window.open(googleMapsLink, "_blank");
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <RouteRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Check Mileage" />
                        </ListItemButton>
                      </ListItem>
                    ) : null}

                    {(interpretationData.Linguist?.Email ||
                      interpretationData.Linguist?.PhoneNumber) && (
                      <Typography
                        sx={{
                          m: "0.5rem 0 0 0.75rem",
                          color: theme.palette.primary.main,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {"Linguist: " +
                          (interpretationData.Linguist?.Name || "N/A")}
                      </Typography>
                    )}

                    {interpretationData.Linguist?.Email !== null ? (
                      <ListItem key="Linguist Email" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            const email = interpretationData.Linguist.Email;
                            const subject =
                              "Regarding Assignment N° " +
                              interpretationData.ProjectNumber;
                            const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
                              subject,
                            )}`;
                            window.open(mailtoLink, "_blank");
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <EmailRoundedIcon
                              sx={{
                                fontSize: 24,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Email" />
                        </ListItemButton>
                      </ListItem>
                    ) : null}

                    {interpretationData.Linguist?.PhoneNumber !== null ? (
                      <ListItem key="Linguist Call" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            const number =
                              interpretationData.Linguist.PhoneNumber;
                            const callLink = `tel:${number}`;
                            window.open(callLink, "_blank");
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <CallRoundedIcon
                              sx={{
                                fontSize: 24,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Call" />
                        </ListItemButton>
                      </ListItem>
                    ) : null}

                    {(interpretationData.Requestor?.Email ||
                      interpretationData.Requestor?.PhoneNumber) && (
                      <Typography
                        sx={{
                          m: "0.5rem 0 0 0.75rem",
                          color: theme.palette.primary.main,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {"Requestor: " +
                          (interpretationData.Requestor?.Name || "N/A")}
                      </Typography>
                    )}

                    {interpretationData.Requestor?.Email ? (
                      <ListItem key="Requestor Email" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            const email = interpretationData.Requestor.Email;
                            const subject =
                              "Regarding Assignment N° " +
                              interpretationData.ProjectNumber;
                            const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
                              subject,
                            )}`;
                            window.open(mailtoLink, "_blank");
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <EmailRoundedIcon
                              sx={{
                                fontSize: 24,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Email" />
                        </ListItemButton>
                      </ListItem>
                    ) : null}

                    {interpretationData.Requestor?.PhoneNumber ? (
                      <ListItem key="Requestor Call" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            const number =
                              interpretationData.Requestor.PhoneNumber;
                            const callLink = buildCallLink(
                              number,
                              interpretationData.Requestor?.Ext,
                            );
                            window.open(callLink, "_blank");
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <CallRoundedIcon
                              sx={{
                                fontSize: 24,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Call" />
                        </ListItemButton>
                      </ListItem>
                    ) : null}

                    {contactPersonsWithPhone.map((contact, index) => (
                      <React.Fragment
                        key={`desktop-contact-call-${contact.id || contact.Name || index}`}
                      >
                        <Typography
                          sx={{
                            m: "0.5rem 0 0 0.75rem",
                            color: theme.palette.primary.main,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {"Contact Person: " + (contact.Name || "N/A")}
                        </Typography>
                        <ListItem disablePadding>
                          <ListItemButton
                            sx={{
                              color: theme.palette.primary.main,
                              borderRadius: "8px",
                              border: `1px solid ${theme.palette.divider}`,
                              mb: 1,
                              "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                            onClick={() => {
                              openCallLink(contact.PhoneNumber, contact.Ext);
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                mr: "-1rem",
                                color: theme.palette.primary.main,
                              }}
                            >
                              <CallRoundedIcon
                                sx={{
                                  fontSize: 24,
                                  color: theme.palette.primary.main,
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText primary="Call" />
                          </ListItemButton>
                        </ListItem>
                      </React.Fragment>
                    ))}

                    <Divider sx={{ my: 2 }} />

                    <Typography
                      sx={{
                        m: "0.25rem 0 0 0.5rem",
                        color: theme.palette.primary.main,
                        fontSize: 13,
                      }}
                    >
                      Utilities
                    </Typography>

                    <ListItem key="CopyTrackerRow" disablePadding>
                      <ListItemButton
                        sx={{
                          color: theme.palette.primary.main,
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.divider}`,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                        onClick={handleCopyTrackerRow}
                      >
                        <ListItemIcon
                          sx={{
                            mr: "-1rem",
                            color: theme.palette.primary.main,
                          }}
                        >
                          <ContentCopyRoundedIcon
                            sx={{
                              fontSize: 24,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary="Copy Tracker Row" />
                      </ListItemButton>
                    </ListItem>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default InterpretationProfile;
